import {
  AxesHelper,
  Box3,
  CanvasTexture,
  GridHelper,
  Group,
  LineBasicMaterial,
  type Object3D,
  PerspectiveCamera,
  Quaternion,
  Raycaster,
  Scene,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Timer,
  Vector2,
  Vector3,
  WebGLRenderer
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ViewHelper } from "three/examples/jsm/helpers/ViewHelper.js";
import type { DxfDevicePlacement } from "../cad/cad-device-layout";
import type { DxfDoubleChainConveyorPlacement } from "../cad/double-chain-conveyor-layout";
import {
  createDxfFloorReference,
  type DxfFloorReference,
  type DxfFloorReferenceMetadata,
  type DxfFloorReferenceStage
} from "../cad/dxf-floor-reference";
import type { DxfBinRackPlacementResult, DxfRackPlacement } from "../cad/dxf-raster.worker";
import type { DxfPalletReciprocatingLiftPlacement } from "../cad/pallet-reciprocating-lift-layout";
import type { DxfPalletStackerCranePlacement } from "../cad/pallet-stacker-crane-layout";
import type { DxfRollerConveyorPlacement } from "../cad/roller-conveyor-layout";
import type { DxfRollerLiftTransferPlacement } from "../cad/roller-lift-transfer-layout";
import type { DxfSingleStationReciprocatingShuttlePlacement } from "../cad/single-station-reciprocating-shuttle-layout";
import {
  type DeviceModelDefinition,
  getDeviceModelsForPrefix
} from "../models/device-model-catalog";
import { type DeviceModelLoader, deviceModelLibrary } from "../models/device-model-library";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { disposeObject3DResources as disposeRenderableObject } from "../models/shared-model-resources";
import { BinRack, type BinRackCargoCell, defaultBinRackLayerHeights } from "../objects/bin-rack";
import { CadDevice } from "../objects/cad-device";
import { DoubleChainConveyor } from "../objects/double-chain-conveyor";
import { applyDxfMirror, refreshDxfMirror } from "../objects/dxf-mirror";
import {
  defaultPalletRackLayerHeights,
  PalletRack,
  type PalletRackCargoCell
} from "../objects/pallet-rack";
import { PalletReciprocatingLift } from "../objects/pallet-reciprocating-lift";
import {
  defaultPalletStackerCraneHeight,
  PalletStackerCrane
} from "../objects/pallet-stacker-crane";
import { RollerConveyor } from "../objects/roller-conveyor";
import { RollerLiftTransfer } from "../objects/roller-lift-transfer";
import {
  defaultSingleStationReciprocatingShuttleHeight,
  SingleStationReciprocatingShuttle
} from "../objects/single-station-reciprocating-shuttle";
import type { ProjectSceneState } from "../projects/project-types";
import {
  type CargoMultiSelectionGroup,
  type CargoMultiSelectionTarget,
  createDeviceSelectionCategory,
  createRackSelectionCategory,
  palletReciprocatingLiftSelectionCategory,
  palletStackerCraneSelectionCategory,
  singleStationReciprocatingShuttleSelectionCategory,
  toggleCargoMultiSelection
} from "./cargo-multi-selection";
import {
  areCargoRenderOptionsEqual,
  type CargoRenderOptions,
  createCargoRenderContext,
  defaultCargoRenderOptions,
  highQualityCargoRenderOptions
} from "./cargo-render-mode";
import { exportCargoSceneGlb } from "./cargo-scene-glb-export";
import { renderCargoSceneImage } from "./cargo-scene-image-render";

export type CargoEditorRackConfiguration = {
  cargoCapacity: number;
  cargoCellLength: number;
  cargoCellWidth: number;
  cargoRowCount: number;
  layerHeights: number[];
  lengthCellCount: number;
};

export type CargoEditorRackSelection = CargoEditorRackConfiguration &
  PalletRackCargoCell & {
    cargo: Array<CargoEditorCargoAsset | undefined>;
    kind: "rack-cell";
    rackId: string;
    rackType: CargoEditorRackType;
  };

export type CargoEditorDeviceSelection = {
  blockPrefix: string;
  deviceId: string;
  kind: "device";
  modelId: string;
  models: DeviceModelDefinition[];
};

export type CargoEditorPalletReciprocatingLiftSelection = {
  kind: "pallet-reciprocating-lift";
  layerCount: number;
  liftId: string;
};

export type CargoEditorSingleStationReciprocatingShuttleSelection = {
  height: number;
  kind: "single-station-reciprocating-shuttle";
  shuttleId: string;
};
export type CargoEditorPalletStackerCraneSelection = {
  height: number;
  kind: "pallet-stacker-crane";
  stackerCraneId: string;
};

export type CargoEditorRackBatchSelection = {
  cargoCapacity: number;
  cargoCapacityMixed: boolean;
  kind: "rack-batch";
  layerHeights: number[];
  layerHeightsMixed: boolean;
  rackIds: string[];
  rackType: CargoEditorRackType;
};

export type CargoEditorDeviceBatchSelection = {
  blockPrefix: string;
  deviceIds: string[];
  kind: "device-batch";
  modelId: string;
  modelMixed: boolean;
  models: DeviceModelDefinition[];
};

export type CargoEditorPalletReciprocatingLiftBatchSelection = {
  kind: "pallet-reciprocating-lift-batch";
  layerCount: number;
  layerCountMixed: boolean;
  liftIds: string[];
};

export type CargoEditorSingleStationReciprocatingShuttleBatchSelection = {
  height: number;
  heightMixed: boolean;
  kind: "single-station-reciprocating-shuttle-batch";
  shuttleIds: string[];
};
export type CargoEditorPalletStackerCraneBatchSelection = {
  height: number;
  heightMixed: boolean;
  kind: "pallet-stacker-crane-batch";
  stackerCraneIds: string[];
};

export type CargoEditorBatchSelection =
  | CargoEditorDeviceBatchSelection
  | CargoEditorPalletReciprocatingLiftBatchSelection
  | CargoEditorRackBatchSelection
  | CargoEditorSingleStationReciprocatingShuttleBatchSelection
  | CargoEditorPalletStackerCraneBatchSelection;

export type CargoEditorBatchSettingRegion = {
  id: string;
  name: string;
  selections: CargoEditorBatchSelection[];
};

export type CargoEditorSelection =
  | CargoEditorBatchSelection
  | CargoEditorDeviceSelection
  | CargoEditorPalletReciprocatingLiftSelection
  | CargoEditorRackSelection
  | CargoEditorSingleStationReciprocatingShuttleSelection
  | CargoEditorPalletStackerCraneSelection;

export type CargoEditorRackType = "bin" | "pallet";

export type CargoEditorCargoAsset =
  | ModelAsset.BinCargo
  | ModelAsset.SteelCoil
  | ModelAsset.WoodenCrate;

export type CargoEditorRackCargoUpdate = {
  asset?: CargoEditorCargoAsset;
  cellId: string;
  heightIndex: number;
  slotIndex: number;
};

export type CargoEditorDxfReference = DxfFloorReferenceMetadata;

export type CargoEditorDxfStage = DxfFloorReferenceStage;

export type CargoEditorCameraState = {
  position: [number, number, number];
  target: [number, number, number];
};

export type CargoEditorSceneImageOptions = {
  includeCad: boolean;
};

export type CargoEditorSceneController = {
  captureCameraState: () => CargoEditorCameraState;
  clearDxfReference: () => void;
  dispose: () => void;
  exportSceneGlb: () => Promise<ArrayBuffer>;
  getBatchSettingRegions: () => CargoEditorBatchSettingRegion[];
  loadDxfReference: (
    file: File,
    onStage?: (stage: CargoEditorDxfStage) => void,
    projectState?: ProjectSceneState
  ) => Promise<CargoEditorDxfReference>;
  getRackCargoUpdatesForSelection: (
    rackIds: string[],
    asset?: CargoEditorCargoAsset
  ) => Map<string, CargoEditorRackCargoUpdate[]> | undefined;
  renderSceneImage: (options: CargoEditorSceneImageOptions) => Promise<Blob>;
  setMultiSelectionMode: (active: boolean) => void;
  setCargo: (
    selection: CargoEditorRackSelection,
    slotIndex: number,
    asset?: CargoEditorCargoAsset
  ) => Promise<void>;
  setRackCargo: (
    rackId: string,
    asset?: CargoEditorCargoAsset
  ) => Promise<CargoEditorRackCargoUpdate[] | undefined>;
  setRackConfiguration: (
    rackId: string,
    cellId: string,
    configuration: CargoEditorRackConfiguration
  ) => Promise<void>;
  setDeviceModel: (deviceId: string, modelId: string) => Promise<void>;
  setDeviceModelsForSelection: (deviceIds: string[], modelId: string) => Promise<void>;
  setDeviceModels: (models: readonly DeviceModelDefinition[]) => Promise<void>;
  setPalletReciprocatingLiftLayerCount: (liftId: string, layerCount: number) => void;
  setPalletReciprocatingLiftLayerCountForSelection: (liftIds: string[], layerCount: number) => void;
  setSingleStationReciprocatingShuttleHeight: (shuttleId: string, height: number) => void;
  setPalletStackerCraneHeight: (stackerCraneId: string, height: number) => void;
  setSingleStationReciprocatingShuttleHeightForSelection: (
    shuttleIds: string[],
    height: number
  ) => void;
  setPalletStackerCraneHeightForSelection: (stackerCraneIds: string[], height: number) => void;
  setRackCargoForSelection: (
    rackIds: string[],
    asset?: CargoEditorCargoAsset
  ) => Promise<Map<string, CargoEditorRackCargoUpdate[]> | undefined>;
  setRackCargoCapacityForSelection: (
    rackIds: string[],
    cargoCapacity: number
  ) => Promise<Map<string, string[]> | undefined>;
  setRackLayerHeightsForSelection: (rackIds: string[], layerHeights: number[]) => Promise<void>;
  setBrowseCameraEditing: (editing: boolean) => void;
  setBrowseMode: (active: boolean) => void;
  setCameraState: (state: CargoEditorCameraState) => void;
  setRenderOptions: (options: CargoRenderOptions) => Promise<void>;
};

type CargoEditorSceneOptions = {
  deviceModels: readonly DeviceModelDefinition[];
  onMultiSelectionModeChange: (active: boolean) => void;
  onSelectionChange: (selection?: CargoEditorSelection) => void;
  renderOptions?: CargoRenderOptions;
};

type DxfRackInstance = {
  cargoAssets: Map<string, CargoEditorCargoAsset>;
  cells: DxfRackPlacement["cells"];
  id: string;
  kind: CargoEditorRackType;
  shelf: BinRack | PalletRack;
};

type DxfRackCollection = {
  group: Group;
  racks: Map<string, DxfRackInstance>;
  sharedCargoTemplate?: Object3D;
};

type DxfDeviceCollection = {
  devices: Map<string, DxfDeviceInstance>;
  group: Group;
};

type DxfDoubleChainConveyorCollection = {
  group: Group;
};

type DxfRollerConveyorCollection = {
  group: Group;
};

type DxfRollerLiftTransferCollection = {
  group: Group;
};

type DxfPalletReciprocatingLiftCollection = {
  group: Group;
  lifts: Map<string, DxfPalletReciprocatingLiftInstance>;
};

type DxfPalletReciprocatingLiftInstance = {
  id: string;
  lift: PalletReciprocatingLift;
};

type DxfSingleStationReciprocatingShuttleCollection = {
  group: Group;
  shuttles: Map<string, DxfSingleStationReciprocatingShuttleInstance>;
};
type DxfPalletStackerCraneCollection = {
  group: Group;
  stackerCranes: Map<string, DxfPalletStackerCraneInstance>;
};

type DxfSingleStationReciprocatingShuttleInstance = {
  id: string;
  shuttle: SingleStationReciprocatingShuttle;
};
type DxfPalletStackerCraneInstance = {
  id: string;
  stackerCrane: PalletStackerCrane;
};

type DxfDeviceInstance = {
  blockPrefix: string;
  device: CadDevice;
  id: string;
};

type DxfRackSnapshot = {
  cargoAssets: Map<string, CargoEditorCargoAsset | null>;
  cargoCapacities: Map<string, number>;
  layerHeightMode?: "clear" | "legacy-span";
  layerHeights?: number[];
};

type DxfSelectionIdentity = {
  cell: BinRackCargoCell | PalletRackCargoCell;
  rackId: string;
};

type DxfDeviceSelectionIdentity = {
  deviceId: string;
  modelId: string;
};

type DxfPalletReciprocatingLiftSelectionIdentity = {
  liftId: string;
};

type DxfSingleStationReciprocatingShuttleSelectionIdentity = {
  shuttleId: string;
};
type DxfPalletStackerCraneSelectionIdentity = {
  stackerCraneId: string;
};

type CargoMultiSelectionPointer = {
  moved: boolean;
  pointerId: number;
  startX: number;
  startY: number;
};

type CargoCameraTransition = {
  currentDirection: Vector3;
  currentRotation: Quaternion;
  directionRotation: Quaternion;
  fromDirection: Vector3;
  fromDistance: number;
  fromTarget: Vector3;
  startedAt: number;
  toDistance: number;
  toPosition: Vector3;
  toTarget: Vector3;
};

const cameraTransitionDurationMs = 700;

export async function createCargoEditorScene(
  host: HTMLDivElement,
  options: CargoEditorSceneOptions
): Promise<CargoEditorSceneController> {
  await modelAssetLibrary.preload();
  return startCargoEditorScene(host, options);
}

function startCargoEditorScene(
  host: HTMLDivElement,
  options: CargoEditorSceneOptions
): CargoEditorSceneController {
  const scene = new Scene();

  const renderer = new WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth, host.clientHeight);
  renderer.autoClear = false;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.localClippingEnabled = true;
  host.appendChild(renderer.domElement);

  const camera = new PerspectiveCamera(
    45,
    host.clientWidth / Math.max(host.clientHeight, 1),
    0.01,
    1000
  );
  camera.position.set(8, 6, 8);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = true;
  controls.enableZoom = true;

  const viewHelper = new ViewHelper(camera, renderer.domElement);
  viewHelper.location = { top: null, right: 16, bottom: 16, left: null };
  viewHelper.setLabels("X", "Y", "Z");
  viewHelper.setLabelStyle("14px ui-sans-serif", "#171717", 16);

  const grid = new GridHelper(24, 48, "#ffffff", "#ffffff");
  grid.position.y = 0.002;
  const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
  gridMaterials.forEach((material) => {
    if (material instanceof LineBasicMaterial) {
      material.transparent = true;
      material.opacity = 0.18;
      material.depthWrite = false;
    }
  });
  scene.add(grid);

  const axes = new AxesHelper(12);
  const axisLabels = [
    createAxisLabel("X", "#ff4d4d", new Vector3(12.4, 0, 0)),
    createAxisLabel("Y", "#6dff6d", new Vector3(0, 12.4, 0)),
    createAxisLabel("Z", "#5d8cff", new Vector3(0, 0, 12.4))
  ];
  scene.add(axes, ...axisLabels);
  let activeRenderOptions = { ...(options.renderOptions ?? defaultCargoRenderOptions) };
  let renderContext = createCargoRenderContext(
    activeRenderOptions,
    scene,
    renderer,
    grid,
    modelAssetLibrary,
    deviceModelLibrary
  );

  const raycaster = new Raycaster();
  const pointer = new Vector2();
  const timer = new Timer();
  timer.connect(document);
  let disposed = false;
  let hoveredTarget: Object3D | undefined;
  let hoveredRack: DxfRackInstance | undefined;
  let selectedCell: BinRackCargoCell | PalletRackCargoCell | undefined;
  let selectedDevice: DxfDeviceInstance | undefined;
  let selectedPalletReciprocatingLift: DxfPalletReciprocatingLiftInstance | undefined;
  let selectedSingleStationReciprocatingShuttle:
    | DxfSingleStationReciprocatingShuttleInstance
    | undefined;
  let selectedPalletStackerCrane: DxfPalletStackerCraneInstance | undefined;
  let selectedRack: DxfRackInstance | undefined;
  let selectedBatch: CargoEditorBatchSelection | undefined;
  let multiSelectionClickAllowed = false;
  let multiSelectionModeActive = false;
  let multiSelectionPointer: CargoMultiSelectionPointer | undefined;
  let activeDeviceModels = [...options.deviceModels];
  let activeDxfFile: File | undefined;
  let dxfReference: DxfFloorReference | undefined;
  let dxfRackCollection: DxfRackCollection | undefined;
  let dxfDeviceCollection: DxfDeviceCollection | undefined;
  let dxfDoubleChainConveyorCollection: DxfDoubleChainConveyorCollection | undefined;
  let dxfRollerConveyorCollection: DxfRollerConveyorCollection | undefined;
  let dxfRollerLiftTransferCollection: DxfRollerLiftTransferCollection | undefined;
  let dxfPalletReciprocatingLiftCollection: DxfPalletReciprocatingLiftCollection | undefined;
  let dxfSingleStationReciprocatingShuttleCollection:
    | DxfSingleStationReciprocatingShuttleCollection
    | undefined;
  let dxfPalletStackerCraneCollection: DxfPalletStackerCraneCollection | undefined;
  let dxfContentGroup: Group | undefined;
  let dxfLoadAbortController: AbortController | undefined;
  let dxfLoadGeneration = 0;
  let frameId: number | undefined;
  let browseCameraEditing = false;
  let browseModeActive = false;
  let cameraTransition: CargoCameraTransition | undefined;
  let cameraLocked = false;
  let isUpdatingRack = false;
  const identityCameraRotation = new Quaternion();
  const deviceModelRequestGenerations = new Map<string, number>();
  let updateQueue: Promise<void> = Promise.resolve();

  function requestRender() {
    if (disposed || cameraLocked || frameId !== undefined) {
      return;
    }
    frameId = window.requestAnimationFrame(render);
  }

  const updateCameraTransition = (timestamp: number) => {
    const transition = cameraTransition;
    if (!transition) {
      return false;
    }

    const progress = Math.min((timestamp - transition.startedAt) / cameraTransitionDurationMs, 1);
    const easedProgress = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
    if (progress >= 1) {
      camera.position.copy(transition.toPosition);
      controls.target.copy(transition.toTarget);
    } else {
      controls.target.lerpVectors(transition.fromTarget, transition.toTarget, easedProgress);
      transition.currentRotation.slerpQuaternions(
        identityCameraRotation,
        transition.directionRotation,
        easedProgress
      );
      transition.currentDirection
        .copy(transition.fromDirection)
        .applyQuaternion(transition.currentRotation);
      const distance =
        transition.fromDistance + (transition.toDistance - transition.fromDistance) * easedProgress;
      camera.position.copy(controls.target).addScaledVector(transition.currentDirection, distance);
    }
    const dampingWasEnabled = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = dampingWasEnabled;
    viewHelper.center.copy(controls.target);

    if (progress >= 1) {
      cameraTransition = undefined;
      return false;
    }
    return true;
  };

  function render(timestamp: number) {
    frameId = undefined;
    timer.update();
    const delta = timer.getDelta();
    const cameraTransitionActive = updateCameraTransition(timestamp);
    const controlsChanged = !cameraLocked && controls.enabled && controls.update();
    if (!cameraLocked && viewHelper.animating) {
      viewHelper.update(delta);
    }
    renderer.clear();
    renderer.render(scene, camera);
    viewHelper.render(renderer);
    if (cameraTransitionActive || controlsChanged || viewHelper.animating) {
      requestRender();
    }
  }

  controls.addEventListener("change", requestRender);

  const clearHover = () => {
    const hadHover = hoveredTarget !== undefined;
    hoveredRack?.shelf.hideCargoCellHighlight();
    hoveredTarget = undefined;
    hoveredRack = undefined;
    renderer.domElement.style.cursor = "";
    if (hadHover) {
      requestRender();
    }
  };

  const setBatchSelectionHighlight = (
    selection: CargoEditorBatchSelection | undefined,
    selected: boolean
  ) => {
    if (!selection) {
      return;
    }
    if (selection.kind === "rack-batch") {
      selection.rackIds.forEach((rackId) => {
        const rack = dxfRackCollection?.racks.get(rackId);
        rack?.shelf.clearCargoCellSelection();
        rack?.shelf.setSelected(selected);
      });
      return;
    }
    if (selection.kind === "device-batch") {
      selection.deviceIds.forEach((deviceId) => {
        dxfDeviceCollection?.devices.get(deviceId)?.device.setSelected(selected);
      });
      return;
    }
    if (selection.kind === "pallet-stacker-crane-batch") {
      selection.stackerCraneIds.forEach((stackerCraneId) => {
        dxfPalletStackerCraneCollection?.stackerCranes
          .get(stackerCraneId)
          ?.stackerCrane.setSelected(selected);
      });
      return;
    }
    if (selection.kind === "single-station-reciprocating-shuttle-batch") {
      selection.shuttleIds.forEach((shuttleId) => {
        dxfSingleStationReciprocatingShuttleCollection?.shuttles
          .get(shuttleId)
          ?.shuttle.setSelected(selected);
      });
      return;
    }
    selection.liftIds.forEach((liftId) => {
      dxfPalletReciprocatingLiftCollection?.lifts.get(liftId)?.lift.setSelected(selected);
    });
  };

  const createBatchSelection = (
    group: CargoMultiSelectionGroup
  ): CargoEditorBatchSelection | undefined => {
    const category = group.category;
    if (category.kind === "rack") {
      const racks = group.ids
        .map((rackId) => dxfRackCollection?.racks.get(rackId))
        .filter((rack): rack is DxfRackInstance => rack?.kind === category.rackType);
      if (racks.length === 0 || racks.length !== group.ids.length) {
        return;
      }
      const capacities = racks.flatMap((rack) =>
        rack.cells.map((cell) => rack.shelf.getCellConfiguration(cell.id).cargoCapacity)
      );
      const firstCapacity = capacities[0] ?? 1;
      const cargoCapacityMixed = capacities.some((capacity) => capacity !== firstCapacity);
      const firstLayerHeights = racks[0]?.shelf.getConfiguration().layerHeights ?? [];
      const layerHeightsMixed = racks.some(
        (rack) => !areEqualNumbers(rack.shelf.getConfiguration().layerHeights, firstLayerHeights)
      );
      return {
        cargoCapacity: cargoCapacityMixed ? 1 : firstCapacity,
        cargoCapacityMixed,
        kind: "rack-batch",
        layerHeights: layerHeightsMixed
          ? [
              ...(category.rackType === "bin"
                ? defaultBinRackLayerHeights
                : defaultPalletRackLayerHeights)
            ]
          : [...firstLayerHeights],
        layerHeightsMixed,
        rackIds: racks.map(({ id }) => id),
        rackType: category.rackType
      };
    }
    if (category.kind === "device") {
      const devices = group.ids
        .map((deviceId) => dxfDeviceCollection?.devices.get(deviceId))
        .filter(
          (device): device is DxfDeviceInstance => device?.blockPrefix === category.blockPrefix
        );
      const models = getDeviceModelsForPrefix(activeDeviceModels, category.blockPrefix);
      if (devices.length === 0 || devices.length !== group.ids.length || models.length === 0) {
        return;
      }
      const firstModelId = devices[0]?.device.getModelId() ?? models[0]?.id ?? "";
      const modelMixed = devices.some(({ device }) => device.getModelId() !== firstModelId);
      return {
        blockPrefix: category.blockPrefix,
        deviceIds: devices.map(({ id }) => id),
        kind: "device-batch",
        modelId: modelMixed ? (models[0]?.id ?? "") : firstModelId,
        modelMixed,
        models
      };
    }

    if (category.kind === "pallet-stacker-crane") {
      const stackerCranes = group.ids
        .map((stackerCraneId) => dxfPalletStackerCraneCollection?.stackerCranes.get(stackerCraneId))
        .filter((stackerCrane): stackerCrane is DxfPalletStackerCraneInstance =>
          Boolean(stackerCrane)
        );
      if (stackerCranes.length === 0 || stackerCranes.length !== group.ids.length) {
        return;
      }
      const firstHeight =
        stackerCranes[0]?.stackerCrane.getHeight() ?? defaultPalletStackerCraneHeight;
      const heightMixed = stackerCranes.some(
        ({ stackerCrane }) => stackerCrane.getHeight() !== firstHeight
      );
      return {
        height: heightMixed ? defaultPalletStackerCraneHeight : firstHeight,
        heightMixed,
        kind: "pallet-stacker-crane-batch",
        stackerCraneIds: stackerCranes.map(({ id }) => id)
      };
    }
    if (category.kind === "single-station-reciprocating-shuttle") {
      const shuttles = group.ids
        .map((shuttleId) => dxfSingleStationReciprocatingShuttleCollection?.shuttles.get(shuttleId))
        .filter((shuttle): shuttle is DxfSingleStationReciprocatingShuttleInstance =>
          Boolean(shuttle)
        );
      if (shuttles.length === 0 || shuttles.length !== group.ids.length) {
        return;
      }
      const firstHeight =
        shuttles[0]?.shuttle.getHeight() ?? defaultSingleStationReciprocatingShuttleHeight;
      const heightMixed = shuttles.some(({ shuttle }) => shuttle.getHeight() !== firstHeight);
      return {
        height: heightMixed ? defaultSingleStationReciprocatingShuttleHeight : firstHeight,
        heightMixed,
        kind: "single-station-reciprocating-shuttle-batch",
        shuttleIds: shuttles.map(({ id }) => id)
      };
    }

    const lifts = group.ids
      .map((liftId) => dxfPalletReciprocatingLiftCollection?.lifts.get(liftId))
      .filter((lift): lift is DxfPalletReciprocatingLiftInstance => Boolean(lift));
    if (lifts.length === 0 || lifts.length !== group.ids.length) {
      return;
    }
    const firstLayerCount = lifts[0]?.lift.getLayerCount() ?? 4;
    const layerCountMixed = lifts.some(({ lift }) => lift.getLayerCount() !== firstLayerCount);
    return {
      kind: "pallet-reciprocating-lift-batch",
      layerCount: layerCountMixed ? 4 : firstLayerCount,
      layerCountMixed,
      liftIds: lifts.map(({ id }) => id)
    };
  };

  const getBatchSettingRegions = (): CargoEditorBatchSettingRegion[] =>
    (dxfReference?.batchSettingRegions ?? []).map((region) => {
      const groups = new Map<string, CargoMultiSelectionGroup>();
      region.members.forEach((member) => {
        const category =
          member.kind === "pallet-rack"
            ? createRackSelectionCategory("pallet")
            : member.kind === "bin-rack"
              ? createRackSelectionCategory("bin")
              : member.kind === "device"
                ? createDeviceSelectionCategory(member.blockPrefix)
                : member.kind === "pallet-stacker-crane"
                  ? palletStackerCraneSelectionCategory
                  : member.kind === "single-station-reciprocating-shuttle"
                    ? singleStationReciprocatingShuttleSelectionCategory
                    : palletReciprocatingLiftSelectionCategory;
        const key =
          category.kind === "rack"
            ? `rack:${category.rackType}`
            : category.kind === "device"
              ? `device:${category.blockPrefix}`
              : category.key;
        const group = groups.get(key);
        if (group) {
          group.ids.push(member.id);
        } else {
          groups.set(key, { category, ids: [member.id] });
        }
      });

      return {
        id: region.id,
        name: region.name,
        selections: [...groups.values()]
          .map(createBatchSelection)
          .filter((selection): selection is CargoEditorBatchSelection => Boolean(selection))
      };
    });

  const applyMultiSelectionGroup = (group: CargoMultiSelectionGroup) => {
    const nextSelection = createBatchSelection(group);
    if (!nextSelection) {
      return;
    }
    clearSelection();
    selectedBatch = nextSelection;
    setBatchSelectionHighlight(selectedBatch, true);
    options.onSelectionChange(selectedBatch);
    requestRender();
  };

  const restoreBatchSelection = (selection: CargoEditorBatchSelection | undefined) => {
    if (!selection) {
      return false;
    }
    const group: CargoMultiSelectionGroup =
      selection.kind === "rack-batch"
        ? {
            category: createRackSelectionCategory(selection.rackType),
            ids: selection.rackIds
          }
        : selection.kind === "device-batch"
          ? {
              category: createDeviceSelectionCategory(selection.blockPrefix),
              ids: selection.deviceIds
            }
          : selection.kind === "pallet-stacker-crane-batch"
            ? {
                category: palletStackerCraneSelectionCategory,
                ids: selection.stackerCraneIds
              }
            : selection.kind === "single-station-reciprocating-shuttle-batch"
              ? {
                  category: singleStationReciprocatingShuttleSelectionCategory,
                  ids: selection.shuttleIds
                }
              : {
                  category: palletReciprocatingLiftSelectionCategory,
                  ids: selection.liftIds
                };
    const restored = createBatchSelection(group);
    if (!restored) {
      selectedBatch = undefined;
      options.onSelectionChange(undefined);
      return false;
    }
    selectedBatch = restored;
    setBatchSelectionHighlight(restored, true);
    options.onSelectionChange(restored);
    return true;
  };

  const clearSelection = () => {
    const hadSelection =
      selectedRack !== undefined ||
      selectedDevice !== undefined ||
      selectedPalletReciprocatingLift !== undefined ||
      selectedSingleStationReciprocatingShuttle !== undefined ||
      selectedPalletStackerCrane !== undefined ||
      selectedBatch !== undefined;
    selectedRack?.shelf.setSelected(false);
    selectedDevice?.device.setSelected(false);
    selectedPalletReciprocatingLift?.lift.setSelected(false);
    selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
    selectedPalletStackerCrane?.stackerCrane.setSelected(false);
    setBatchSelectionHighlight(selectedBatch, false);
    selectedCell = undefined;
    selectedDevice = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedPalletStackerCrane = undefined;
    selectedRack = undefined;
    selectedBatch = undefined;
    options.onSelectionChange(undefined);
    if (hadSelection) {
      requestRender();
    }
  };

  const hideInteractionHighlights = () => {
    const hoveredShelf = hoveredRack?.shelf;
    const hoveredCellTarget = hoveredTarget;
    const activeSelectedRack = selectedRack;
    const activeSelectedCell = selectedCell;
    const activeSelectedDevice = selectedDevice;
    const activeSelectedPalletReciprocatingLift = selectedPalletReciprocatingLift;
    const activeSelectedSingleStationReciprocatingShuttle =
      selectedSingleStationReciprocatingShuttle;
    const activeSelectedPalletStackerCrane = selectedPalletStackerCrane;
    const activeSelectedBatch = selectedBatch;

    hoveredShelf?.hideCargoCellHighlight();
    activeSelectedRack?.shelf.clearCargoCellSelection();
    activeSelectedRack?.shelf.setSelected(false);
    activeSelectedDevice?.device.setSelected(false);
    activeSelectedPalletReciprocatingLift?.lift.setSelected(false);
    activeSelectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
    activeSelectedPalletStackerCrane?.stackerCrane.setSelected(false);
    setBatchSelectionHighlight(activeSelectedBatch, false);

    return () => {
      setBatchSelectionHighlight(activeSelectedBatch, true);
      if (activeSelectedDevice) {
        activeSelectedDevice.device.setSelected(true);
      } else if (activeSelectedPalletReciprocatingLift) {
        activeSelectedPalletReciprocatingLift.lift.setSelected(true);
      } else if (activeSelectedPalletStackerCrane) {
        activeSelectedPalletStackerCrane.stackerCrane.setSelected(true);
      } else if (activeSelectedSingleStationReciprocatingShuttle) {
        activeSelectedSingleStationReciprocatingShuttle.shuttle.setSelected(true);
      } else if (activeSelectedRack && activeSelectedCell) {
        const target = activeSelectedRack.shelf.getCargoCellHitTarget(activeSelectedCell);
        activeSelectedRack.shelf.setSelected(true);
        if (target) {
          activeSelectedRack.shelf.selectCargoCell(target);
        }
      }
      if (hoveredShelf && hoveredCellTarget) {
        hoveredShelf.showCargoCellHighlight(hoveredCellTarget);
      }
      requestRender();
    };
  };

  const findCargoCellHit = (event: MouseEvent | PointerEvent) => {
    const rackCollection = dxfRackCollection;
    if (!rackCollection || isUpdatingRack) {
      return;
    }

    const bounds = renderer.domElement.getBoundingClientRect();

    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const racks = [...rackCollection.racks.values()];
    const hitTargets: Object3D[] = [];
    racks.forEach(({ shelf }) => {
      hitTargets.push(...shelf.getCargoCellHitTargets());
    });
    const intersection = raycaster.intersectObjects(hitTargets, false)[0];
    if (!intersection) {
      return;
    }
    const target = intersection.object;

    for (const rack of racks) {
      const cell = rack.shelf.getCargoCell(target);
      if (cell) {
        return { cell, distance: intersection.distance, rack, target };
      }
    }
  };

  const findDeviceHit = (event: MouseEvent | PointerEvent) => {
    const deviceCollection = dxfDeviceCollection;
    if (!deviceCollection || isUpdatingRack) {
      return;
    }

    const bounds = renderer.domElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const devices = [...deviceCollection.devices.values()];
    const hitTargets = devices.flatMap(({ device }) => device.getHitTargets());
    const intersection = raycaster.intersectObjects(hitTargets, false)[0];
    if (!intersection) {
      return;
    }

    return devices
      .filter(({ device }) => device.getHitTargets().includes(intersection.object))
      .map((instance) => ({ distance: intersection.distance, instance }))[0];
  };

  const findPalletReciprocatingLiftHit = (event: MouseEvent | PointerEvent) => {
    const liftCollection = dxfPalletReciprocatingLiftCollection;
    if (!liftCollection || isUpdatingRack) {
      return;
    }

    const bounds = renderer.domElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const lifts = [...liftCollection.lifts.values()];
    const hitTargets = lifts.flatMap(({ lift }) => lift.getHitTargets());
    const intersection = raycaster.intersectObjects(hitTargets, false)[0];
    if (!intersection) {
      return;
    }

    return lifts
      .filter(({ lift }) => lift.getHitTargets().includes(intersection.object))
      .map((instance) => ({ distance: intersection.distance, instance }))[0];
  };

  const findSingleStationReciprocatingShuttleHit = (event: MouseEvent | PointerEvent) => {
    const shuttleCollection = dxfSingleStationReciprocatingShuttleCollection;
    if (!shuttleCollection || isUpdatingRack) {
      return;
    }

    const bounds = renderer.domElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const shuttles = [...shuttleCollection.shuttles.values()];
    const hitTargets = shuttles.flatMap(({ shuttle }) => shuttle.getHitTargets());
    const intersection = raycaster.intersectObjects(hitTargets, false)[0];
    if (!intersection) {
      return;
    }

    return shuttles
      .filter(({ shuttle }) => shuttle.getHitTargets().includes(intersection.object))
      .map((instance) => ({ distance: intersection.distance, instance }))[0];
  };
  const findPalletStackerCraneHit = (event: MouseEvent | PointerEvent) => {
    const stackerCraneCollection = dxfPalletStackerCraneCollection;
    if (!stackerCraneCollection || isUpdatingRack) {
      return;
    }

    const bounds = renderer.domElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const stackerCranes = [...stackerCraneCollection.stackerCranes.values()];
    const hitTargets = stackerCranes.flatMap(({ stackerCrane }) => stackerCrane.getHitTargets());
    const intersection = raycaster.intersectObjects(hitTargets, false)[0];
    if (!intersection) {
      return;
    }

    return stackerCranes
      .filter(({ stackerCrane }) => stackerCrane.getHitTargets().includes(intersection.object))
      .map((instance) => ({ distance: intersection.distance, instance }))[0];
  };

  const getSelectedBatchGroup = (): CargoMultiSelectionGroup | undefined => {
    if (!selectedBatch) {
      return;
    }
    if (selectedBatch.kind === "rack-batch") {
      return {
        category: createRackSelectionCategory(selectedBatch.rackType),
        ids: selectedBatch.rackIds
      };
    }
    if (selectedBatch.kind === "device-batch") {
      return {
        category: createDeviceSelectionCategory(selectedBatch.blockPrefix),
        ids: selectedBatch.deviceIds
      };
    }
    if (selectedBatch.kind === "pallet-stacker-crane-batch") {
      return {
        category: palletStackerCraneSelectionCategory,
        ids: selectedBatch.stackerCraneIds
      };
    }
    if (selectedBatch.kind === "single-station-reciprocating-shuttle-batch") {
      return {
        category: singleStationReciprocatingShuttleSelectionCategory,
        ids: selectedBatch.shuttleIds
      };
    }
    return {
      category: palletReciprocatingLiftSelectionCategory,
      ids: selectedBatch.liftIds
    };
  };

  const findMultiSelectionTarget = (
    event: MouseEvent | PointerEvent
  ): CargoMultiSelectionTarget | undefined => {
    const cargoHit = findCargoCellHit(event);
    const deviceHit = findDeviceHit(event);
    const liftHit = findPalletReciprocatingLiftHit(event);
    const shuttleHit = findSingleStationReciprocatingShuttleHit(event);
    const stackerCraneHit = findPalletStackerCraneHit(event);
    const hits: Array<CargoMultiSelectionTarget & { distance: number }> = [];
    if (cargoHit) {
      hits.push({
        category: createRackSelectionCategory(cargoHit.rack.kind),
        distance: cargoHit.distance,
        id: cargoHit.rack.id
      });
    }
    if (deviceHit) {
      hits.push({
        category: createDeviceSelectionCategory(deviceHit.instance.blockPrefix),
        distance: deviceHit.distance,
        id: deviceHit.instance.id
      });
    }
    if (liftHit) {
      hits.push({
        category: palletReciprocatingLiftSelectionCategory,
        distance: liftHit.distance,
        id: liftHit.instance.id
      });
    }
    if (stackerCraneHit) {
      hits.push({
        category: palletStackerCraneSelectionCategory,
        distance: stackerCraneHit.distance,
        id: stackerCraneHit.instance.id
      });
    }
    if (shuttleHit) {
      hits.push({
        category: singleStationReciprocatingShuttleSelectionCategory,
        distance: shuttleHit.distance,
        id: shuttleHit.instance.id
      });
    }
    hits.sort((a, b) => a.distance - b.distance);
    return hits[0];
  };

  const toggleMultiSelectionTarget = (target: CargoMultiSelectionTarget) => {
    const result = toggleCargoMultiSelection(getSelectedBatchGroup(), target);
    if (result.rejected) {
      return;
    }
    if (!result.group) {
      clearSelection();
      return;
    }
    applyMultiSelectionGroup(result.group);
  };

  const updateMultiSelectionMode = (active: boolean) => {
    const nextActive = active && !cameraLocked && !browseModeActive && !disposed;
    if (multiSelectionModeActive === nextActive) {
      return;
    }
    multiSelectionModeActive = nextActive;
    multiSelectionClickAllowed = false;
    multiSelectionPointer = undefined;
    if (!nextActive || !selectedBatch) {
      clearSelection();
    }
    options.onMultiSelectionModeChange(nextActive);
  };

  const handleMultiSelectionPointerDown = (event: PointerEvent) => {
    if (!multiSelectionModeActive || event.button !== 0) {
      return;
    }
    multiSelectionClickAllowed = false;
    multiSelectionPointer = {
      moved: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY
    };
  };

  const handleMultiSelectionPointerUp = (event: PointerEvent) => {
    const activePointer = multiSelectionPointer;
    if (!activePointer || activePointer.pointerId !== event.pointerId) {
      return;
    }
    multiSelectionPointer = undefined;
    multiSelectionClickAllowed = !activePointer.moved;
  };

  const handleMultiSelectionPointerCancel = (event: PointerEvent) => {
    if (multiSelectionPointer?.pointerId !== event.pointerId) {
      return;
    }
    multiSelectionPointer = undefined;
    multiSelectionClickAllowed = false;
  };

  const handleMultiSelectionKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && multiSelectionModeActive) {
      event.preventDefault();
      updateMultiSelectionMode(false);
      return;
    }
    if (
      event.key.toLowerCase() !== "b" ||
      event.repeat ||
      event.isComposing ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      isEditableKeyboardTarget(event.target)
    ) {
      return;
    }
    event.preventDefault();
    updateMultiSelectionMode(!multiSelectionModeActive);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (multiSelectionPointer?.pointerId === event.pointerId) {
      const distance = Math.hypot(
        event.clientX - multiSelectionPointer.startX,
        event.clientY - multiSelectionPointer.startY
      );
      if (distance > 4) {
        multiSelectionPointer.moved = true;
        multiSelectionClickAllowed = false;
      }
    }
    if (cameraLocked || browseModeActive) {
      return;
    }
    const cargoHit = findCargoCellHit(event);
    const deviceHit = findDeviceHit(event);
    const liftHit = findPalletReciprocatingLiftHit(event);
    const shuttleHit = findSingleStationReciprocatingShuttleHit(event);
    const stackerCraneHit = findPalletStackerCraneHit(event);
    const nearestDistance = Math.min(
      cargoHit?.distance ?? Number.POSITIVE_INFINITY,
      deviceHit?.distance ?? Number.POSITIVE_INFINITY,
      liftHit?.distance ?? Number.POSITIVE_INFINITY,
      shuttleHit?.distance ?? Number.POSITIVE_INFINITY,
      stackerCraneHit?.distance ?? Number.POSITIVE_INFINITY
    );

    if (!Number.isFinite(nearestDistance)) {
      clearHover();
      return;
    }

    const nextHoveredTarget =
      stackerCraneHit?.distance === nearestDistance
        ? stackerCraneHit.instance.stackerCrane
        : shuttleHit?.distance === nearestDistance
          ? shuttleHit.instance.shuttle
          : liftHit?.distance === nearestDistance
            ? liftHit.instance.lift
            : deviceHit?.distance === nearestDistance
              ? deviceHit.instance.device
              : cargoHit?.target;
    if (!nextHoveredTarget || hoveredTarget === nextHoveredTarget) {
      return;
    }

    clearHover();
    if (cargoHit && cargoHit.distance === nearestDistance) {
      cargoHit.rack.shelf.showCargoCellHighlight(cargoHit.target);
      hoveredRack = cargoHit.rack;
    }
    hoveredTarget = nextHoveredTarget;
    renderer.domElement.style.cursor = "pointer";
    requestRender();
  };

  const handleDoubleClick = (event: MouseEvent) => {
    if (cameraLocked || browseModeActive || multiSelectionModeActive) {
      return;
    }
    const cargoHit = findCargoCellHit(event);
    const deviceHit = findDeviceHit(event);
    const liftHit = findPalletReciprocatingLiftHit(event);
    const shuttleHit = findSingleStationReciprocatingShuttleHit(event);
    const stackerCraneHit = findPalletStackerCraneHit(event);
    if (!cargoHit && !deviceHit && !liftHit && !shuttleHit && !stackerCraneHit) {
      return;
    }
    setBatchSelectionHighlight(selectedBatch, false);
    selectedBatch = undefined;
    if (
      stackerCraneHit &&
      (!cargoHit || stackerCraneHit.distance < cargoHit.distance) &&
      (!deviceHit || stackerCraneHit.distance < deviceHit.distance) &&
      (!liftHit || stackerCraneHit.distance < liftHit.distance) &&
      (!shuttleHit || stackerCraneHit.distance <= shuttleHit.distance)
    ) {
      clearSelection();
      selectedPalletStackerCrane = stackerCraneHit.instance;
      selectedPalletStackerCrane.stackerCrane.setSelected(true);
      options.onSelectionChange(createPalletStackerCraneSelection(selectedPalletStackerCrane));
      requestRender();
      return;
    }
    if (
      shuttleHit &&
      (!cargoHit || shuttleHit.distance < cargoHit.distance) &&
      (!deviceHit || shuttleHit.distance < deviceHit.distance) &&
      (!liftHit || shuttleHit.distance < liftHit.distance)
    ) {
      selectedRack?.shelf.setSelected(false);
      selectedDevice?.device.setSelected(false);
      selectedPalletReciprocatingLift?.lift.setSelected(false);
      selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
      selectedPalletStackerCrane?.stackerCrane.setSelected(false);
      selectedRack = undefined;
      selectedCell = undefined;
      selectedDevice = undefined;
      selectedPalletReciprocatingLift = undefined;
      selectedSingleStationReciprocatingShuttle = shuttleHit.instance;
      selectedPalletStackerCrane = undefined;
      selectedSingleStationReciprocatingShuttle.shuttle.setSelected(true);
      options.onSelectionChange(
        createSingleStationReciprocatingShuttleSelection(selectedSingleStationReciprocatingShuttle)
      );
      requestRender();
      return;
    }
    if (
      liftHit &&
      (!cargoHit || liftHit.distance < cargoHit.distance) &&
      (!deviceHit || liftHit.distance < deviceHit.distance) &&
      (!shuttleHit || liftHit.distance < shuttleHit.distance)
    ) {
      selectedRack?.shelf.setSelected(false);
      selectedDevice?.device.setSelected(false);
      selectedPalletReciprocatingLift?.lift.setSelected(false);
      selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
      selectedPalletStackerCrane?.stackerCrane.setSelected(false);
      selectedRack = undefined;
      selectedCell = undefined;
      selectedDevice = undefined;
      selectedSingleStationReciprocatingShuttle = undefined;
      selectedPalletStackerCrane = undefined;
      selectedPalletReciprocatingLift = liftHit.instance;
      selectedPalletReciprocatingLift.lift.setSelected(true);
      options.onSelectionChange(
        createPalletReciprocatingLiftSelection(selectedPalletReciprocatingLift)
      );
      requestRender();
      return;
    }
    if (
      deviceHit &&
      (!cargoHit || deviceHit.distance < cargoHit.distance) &&
      (!shuttleHit || deviceHit.distance < shuttleHit.distance)
    ) {
      selectedRack?.shelf.setSelected(false);
      selectedDevice?.device.setSelected(false);
      selectedPalletReciprocatingLift?.lift.setSelected(false);
      selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
      selectedPalletStackerCrane?.stackerCrane.setSelected(false);
      selectedRack = undefined;
      selectedCell = undefined;
      selectedPalletReciprocatingLift = undefined;
      selectedSingleStationReciprocatingShuttle = undefined;
      selectedPalletStackerCrane = undefined;
      selectedDevice = deviceHit.instance;
      selectedDevice.device.setSelected(true);
      options.onSelectionChange(createDeviceSelection(selectedDevice, activeDeviceModels));
      requestRender();
      return;
    }

    const hit = cargoHit;

    if (!hit) {
      return;
    }

    selectedDevice?.device.setSelected(false);
    selectedPalletReciprocatingLift?.lift.setSelected(false);
    selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
    selectedPalletStackerCrane?.stackerCrane.setSelected(false);
    selectedDevice = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedPalletStackerCrane = undefined;
    if (selectedRack !== hit.rack) {
      selectedRack?.shelf.setSelected(false);
    }

    hit.rack.shelf.setSelected(true);
    hit.rack.shelf.selectCargoCell(hit.target);
    selectedRack = hit.rack;
    selectedCell = hit.cell;
    options.onSelectionChange(createSelection(hit.rack, hit.cell));
    requestRender();
  };

  const handleClick = (event: MouseEvent) => {
    if (cameraLocked || (browseModeActive && !browseCameraEditing)) {
      return;
    }
    if (multiSelectionModeActive) {
      const canSelect = multiSelectionClickAllowed && event.detail === 1;
      multiSelectionClickAllowed = false;
      if (!canSelect) {
        return;
      }
      const target = findMultiSelectionTarget(event);
      if (target) {
        toggleMultiSelectionTarget(target);
        return;
      }
    }
    viewHelper.handleClick(event);
    requestRender();
  };

  const handlePointerLeave = () => {
    if (cameraLocked || browseModeActive) {
      return;
    }
    clearHover();
  };

  renderer.domElement.addEventListener("pointermove", handlePointerMove);
  renderer.domElement.addEventListener("pointerdown", handleMultiSelectionPointerDown, true);
  renderer.domElement.addEventListener("pointerup", handleMultiSelectionPointerUp, true);
  renderer.domElement.addEventListener("pointercancel", handleMultiSelectionPointerCancel, true);
  renderer.domElement.addEventListener(
    "lostpointercapture",
    handleMultiSelectionPointerCancel,
    true
  );
  renderer.domElement.addEventListener("dblclick", handleDoubleClick);
  renderer.domElement.addEventListener("click", handleClick);
  renderer.domElement.addEventListener("pointerleave", handlePointerLeave);
  window.addEventListener("keydown", handleMultiSelectionKeyDown);

  viewHelper.center.copy(controls.target);

  const resizeRendererToHost = () => {
    const width = Math.max(host.clientWidth, 1);
    const height = Math.max(host.clientHeight, 1);
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    requestRender();
  };

  const resizeObserver = new ResizeObserver(() => {
    if (!cameraLocked) {
      resizeRendererToHost();
    }
  });
  resizeObserver.observe(host);
  requestRender();

  const enqueueUpdate = <T>(update: () => Promise<T>) => {
    const nextUpdate = updateQueue.then(update);
    updateQueue = nextUpdate.then(
      () => undefined,
      () => undefined
    );
    return nextUpdate;
  };

  const isActiveRack = (rack: DxfRackInstance) => dxfRackCollection?.racks.get(rack.id) === rack;
  const isActiveDevice = (device: DxfDeviceInstance) =>
    dxfDeviceCollection?.devices.get(device.id) === device;

  const releaseDxfContent = (
    contentGroup: Group,
    rackCollection: DxfRackCollection,
    deviceCollection: DxfDeviceCollection,
    doubleChainConveyorCollection: DxfDoubleChainConveyorCollection,
    rollerConveyorCollection: DxfRollerConveyorCollection,
    rollerLiftTransferCollection: DxfRollerLiftTransferCollection,
    palletReciprocatingLiftCollection: DxfPalletReciprocatingLiftCollection,
    singleStationReciprocatingShuttleCollection: DxfSingleStationReciprocatingShuttleCollection,
    palletStackerCraneCollection: DxfPalletStackerCraneCollection
  ) => {
    const pendingUpdates = updateQueue;
    contentGroup.removeFromParent();
    void pendingUpdates.finally(() => {
      disposeDxfRackCollection(rackCollection);
      disposeDxfDeviceCollection(deviceCollection);
      disposeDxfDoubleChainConveyorCollection(doubleChainConveyorCollection);
      disposeDxfRollerConveyorCollection(rollerConveyorCollection);
      disposeDxfRollerLiftTransferCollection(rollerLiftTransferCollection);
      disposeDxfPalletReciprocatingLiftCollection(palletReciprocatingLiftCollection);
      disposeDxfSingleStationReciprocatingShuttleCollection(
        singleStationReciprocatingShuttleCollection
      );
      disposeDxfPalletStackerCraneCollection(palletStackerCraneCollection);
    });
  };

  const restoreSelection = (
    identity: DxfSelectionIdentity | undefined,
    rackCollection: DxfRackCollection
  ) => {
    selectedDevice = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedPalletStackerCrane = undefined;
    selectedRack = identity ? rackCollection.racks.get(identity.rackId) : undefined;
    selectedCell = selectedRack && identity ? identity.cell : undefined;
    if (!selectedRack || !selectedCell) {
      options.onSelectionChange(undefined);
      return;
    }

    const target = selectedRack.shelf.getCargoCellHitTarget(selectedCell);
    if (!target) {
      selectedRack = undefined;
      selectedCell = undefined;
      options.onSelectionChange(undefined);
      return;
    }

    selectedRack.shelf.setSelected(true);
    selectedRack.shelf.selectCargoCell(target);
    options.onSelectionChange(createSelection(selectedRack, selectedCell));
  };

  const restoreDeviceSelection = (
    identity: DxfDeviceSelectionIdentity | undefined,
    deviceCollection: DxfDeviceCollection
  ) => {
    selectedRack = undefined;
    selectedCell = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedPalletStackerCrane = undefined;
    selectedDevice = identity ? deviceCollection.devices.get(identity.deviceId) : undefined;
    if (!selectedDevice) {
      options.onSelectionChange(undefined);
      return;
    }

    selectedDevice.device.setSelected(true);
    options.onSelectionChange(createDeviceSelection(selectedDevice, activeDeviceModels));
  };

  const restorePalletReciprocatingLiftSelection = (
    identity: DxfPalletReciprocatingLiftSelectionIdentity | undefined,
    collection: DxfPalletReciprocatingLiftCollection
  ) => {
    selectedRack = undefined;
    selectedCell = undefined;
    selectedDevice = undefined;
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedPalletStackerCrane = undefined;
    selectedPalletReciprocatingLift = identity ? collection.lifts.get(identity.liftId) : undefined;
    if (!selectedPalletReciprocatingLift) {
      options.onSelectionChange(undefined);
      return;
    }

    selectedPalletReciprocatingLift.lift.setSelected(true);
    options.onSelectionChange(
      createPalletReciprocatingLiftSelection(selectedPalletReciprocatingLift)
    );
  };

  const restoreSingleStationReciprocatingShuttleSelection = (
    identity: DxfSingleStationReciprocatingShuttleSelectionIdentity | undefined,
    collection: DxfSingleStationReciprocatingShuttleCollection
  ) => {
    selectedPalletStackerCrane = undefined;
    selectedRack = undefined;
    selectedCell = undefined;
    selectedDevice = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedSingleStationReciprocatingShuttle = identity
      ? collection.shuttles.get(identity.shuttleId)
      : undefined;
    if (!selectedSingleStationReciprocatingShuttle) {
      options.onSelectionChange(undefined);
      return;
    }

    selectedSingleStationReciprocatingShuttle.shuttle.setSelected(true);
    options.onSelectionChange(
      createSingleStationReciprocatingShuttleSelection(selectedSingleStationReciprocatingShuttle)
    );
  };
  const restorePalletStackerCraneSelection = (
    identity: DxfPalletStackerCraneSelectionIdentity | undefined,
    collection: DxfPalletStackerCraneCollection
  ) => {
    selectedSingleStationReciprocatingShuttle = undefined;
    selectedRack = undefined;
    selectedCell = undefined;
    selectedDevice = undefined;
    selectedPalletReciprocatingLift = undefined;
    selectedPalletStackerCrane = identity
      ? collection.stackerCranes.get(identity.stackerCraneId)
      : undefined;
    if (!selectedPalletStackerCrane) {
      options.onSelectionChange(undefined);
      return;
    }

    selectedPalletStackerCrane.stackerCrane.setSelected(true);
    options.onSelectionChange(createPalletStackerCraneSelection(selectedPalletStackerCrane));
  };

  const lockCamera = () => {
    const position = camera.position.clone();
    const quaternion = camera.quaternion.clone();
    const target = controls.target.clone();
    const controlsWereEnabled = controls.enabled;
    const dampingWasEnabled = controls.enableDamping;
    cameraTransition = undefined;
    cameraLocked = true;
    if (frameId !== undefined) {
      window.cancelAnimationFrame(frameId);
      frameId = undefined;
    }
    viewHelper.animating = false;
    controls.enabled = false;
    controls.enableDamping = false;
    controls.update();
    camera.position.copy(position);
    camera.quaternion.copy(quaternion);
    controls.target.copy(target);
    controls.update();
    controls.enableDamping = dampingWasEnabled;
    viewHelper.center.copy(target);

    return () => {
      if (disposed) {
        return;
      }
      controls.enableDamping = false;
      camera.position.copy(position);
      camera.quaternion.copy(quaternion);
      controls.target.copy(target);
      controls.update();
      controls.enableDamping = dampingWasEnabled;
      controls.enabled = controlsWereEnabled;
      viewHelper.center.copy(target);
      cameraLocked = false;
      resizeRendererToHost();
    };
  };

  const setControlsEnabled = (enabled: boolean) => {
    const dampingWasEnabled = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = dampingWasEnabled;
    controls.enabled = enabled;
  };

  const applyCameraState = ({ position, target }: CargoEditorCameraState) => {
    const fromTarget = controls.target.clone();
    const toTarget = new Vector3().fromArray(target);
    const toPosition = new Vector3().fromArray(position);
    const fromOffset = camera.position.clone().sub(fromTarget);
    const toOffset = toPosition.clone().sub(toTarget);
    const fromDistance = fromOffset.length();
    const toDistance = toOffset.length();
    const fromDirection = fromOffset.normalize();
    const toDirection = toOffset.normalize();
    cameraTransition = {
      currentDirection: new Vector3(),
      currentRotation: new Quaternion(),
      directionRotation: new Quaternion().setFromUnitVectors(fromDirection, toDirection),
      fromDirection,
      fromDistance,
      fromTarget,
      startedAt: performance.now(),
      toDistance,
      toPosition,
      toTarget
    };
    requestRender();
  };

  const applyRenderOptions = async (nextOptions: CargoRenderOptions) => {
    const requestedOptions = { ...nextOptions };
    if (disposed || areCargoRenderOptionsEqual(activeRenderOptions, requestedOptions)) {
      return;
    }

    const previousOptions = activeRenderOptions;
    const previousContext = renderContext;
    const currentTarget = dxfContentGroup ?? grid;
    const nextContext = createCargoRenderContext(
      requestedOptions,
      scene,
      renderer,
      currentTarget,
      modelAssetLibrary,
      deviceModelLibrary
    );
    previousContext.dispose();

    if (
      !dxfReference ||
      !dxfRackCollection ||
      !dxfDeviceCollection ||
      !dxfDoubleChainConveyorCollection ||
      !dxfRollerConveyorCollection ||
      !dxfRollerLiftTransferCollection ||
      !dxfPalletReciprocatingLiftCollection ||
      !dxfSingleStationReciprocatingShuttleCollection ||
      !dxfPalletStackerCraneCollection ||
      !dxfContentGroup
    ) {
      activeRenderOptions = requestedOptions;
      renderContext = nextContext;
      requestRender();
      return;
    }

    isUpdatingRack = true;
    const reference = dxfReference;
    const previousRackCollection = dxfRackCollection;
    const previousDeviceCollection = dxfDeviceCollection;
    const previousDoubleChainConveyorCollection = dxfDoubleChainConveyorCollection;
    const previousRollerConveyorCollection = dxfRollerConveyorCollection;
    const previousRollerLiftTransferCollection = dxfRollerLiftTransferCollection;
    const previousPalletReciprocatingLiftCollection = dxfPalletReciprocatingLiftCollection;
    const previousSingleStationReciprocatingShuttleCollection =
      dxfSingleStationReciprocatingShuttleCollection;
    const previousPalletStackerCraneCollection = dxfPalletStackerCraneCollection;
    const previousContentGroup = dxfContentGroup;
    const rackSnapshots = captureDxfRackSnapshots(reference, previousRackCollection);
    const deviceSnapshots = captureDxfDeviceSnapshots(previousDeviceCollection);
    const palletReciprocatingLiftSnapshots = captureDxfPalletReciprocatingLiftSnapshots(
      previousPalletReciprocatingLiftCollection
    );
    const singleStationReciprocatingShuttleSnapshots =
      captureDxfSingleStationReciprocatingShuttleSnapshots(
        previousSingleStationReciprocatingShuttleCollection
      );
    const palletStackerCraneSnapshots = captureDxfPalletStackerCraneSnapshots(
      previousPalletStackerCraneCollection
    );
    const selectedIdentity =
      selectedRack && selectedCell
        ? { cell: { ...selectedCell }, rackId: selectedRack.id }
        : undefined;
    const selectedDeviceIdentity = selectedDevice
      ? { deviceId: selectedDevice.id, modelId: selectedDevice.device.getModelId() }
      : undefined;
    const selectedPalletReciprocatingLiftIdentity = selectedPalletReciprocatingLift
      ? { liftId: selectedPalletReciprocatingLift.id }
      : undefined;
    const selectedSingleStationReciprocatingShuttleIdentity =
      selectedSingleStationReciprocatingShuttle
        ? { shuttleId: selectedSingleStationReciprocatingShuttle.id }
        : undefined;
    const selectedPalletStackerCraneIdentity = selectedPalletStackerCrane
      ? { stackerCraneId: selectedPalletStackerCrane.id }
      : undefined;
    const selectedBatchIdentity = selectedBatch;
    let nextRackCollection: DxfRackCollection | undefined;
    let nextDeviceCollection: DxfDeviceCollection | undefined;
    let nextDoubleChainConveyorCollection: DxfDoubleChainConveyorCollection | undefined;
    let nextRollerConveyorCollection: DxfRollerConveyorCollection | undefined;
    let nextRollerLiftTransferCollection: DxfRollerLiftTransferCollection | undefined;
    let nextPalletReciprocatingLiftCollection: DxfPalletReciprocatingLiftCollection | undefined;
    let nextSingleStationReciprocatingShuttleCollection:
      | DxfSingleStationReciprocatingShuttleCollection
      | undefined;
    let nextPalletStackerCraneCollection: DxfPalletStackerCraneCollection | undefined;

    try {
      nextRackCollection = await createDxfRackCollection(
        reference.rackPlacements,
        reference.binRackPlacements,
        nextContext.modelAssetLoader,
        nextContext.maxCargoPrimitiveTriangles,
        nextContext.prepareObject,
        rackSnapshots
      );
      nextDeviceCollection = await createDxfDeviceCollection(
        reference.devicePlacements,
        activeDeviceModels,
        nextContext.deviceModelLoader,
        deviceSnapshots
      );
      nextDoubleChainConveyorCollection = await createDxfDoubleChainConveyorCollection(
        reference.doubleChainConveyorPlacements,
        nextContext.modelAssetLoader,
        nextContext.prepareObject
      );
      nextRollerConveyorCollection = await createDxfRollerConveyorCollection(
        reference.rollerConveyorPlacements,
        nextContext.modelAssetLoader,
        nextContext.prepareObject
      );
      nextRollerLiftTransferCollection = await createDxfRollerLiftTransferCollection(
        reference.rollerLiftTransferPlacements,
        nextContext.modelAssetLoader,
        nextContext.prepareObject
      );
      nextPalletReciprocatingLiftCollection = await createDxfPalletReciprocatingLiftCollection(
        reference.palletReciprocatingLiftPlacements,
        nextContext.modelAssetLoader,
        nextContext.prepareObject,
        palletReciprocatingLiftSnapshots
      );
      nextSingleStationReciprocatingShuttleCollection =
        await createDxfSingleStationReciprocatingShuttleCollection(
          reference.singleStationReciprocatingShuttlePlacements,
          nextContext.modelAssetLoader,
          nextContext.prepareObject,
          singleStationReciprocatingShuttleSnapshots
        );
      nextPalletStackerCraneCollection = await createDxfPalletStackerCraneCollection(
        reference.palletStackerCranePlacements,
        nextContext.modelAssetLoader,
        nextContext.prepareObject,
        palletStackerCraneSnapshots
      );
    } catch (error) {
      if (nextRackCollection) {
        disposeDxfRackCollection(nextRackCollection);
      }
      if (nextDeviceCollection) {
        disposeDxfDeviceCollection(nextDeviceCollection);
      }
      if (nextDoubleChainConveyorCollection) {
        disposeDxfDoubleChainConveyorCollection(nextDoubleChainConveyorCollection);
      }
      if (nextRollerConveyorCollection) {
        disposeDxfRollerConveyorCollection(nextRollerConveyorCollection);
      }
      if (nextRollerLiftTransferCollection) {
        disposeDxfRollerLiftTransferCollection(nextRollerLiftTransferCollection);
      }
      if (nextPalletReciprocatingLiftCollection) {
        disposeDxfPalletReciprocatingLiftCollection(nextPalletReciprocatingLiftCollection);
      }
      if (nextPalletStackerCraneCollection) {
        disposeDxfPalletStackerCraneCollection(nextPalletStackerCraneCollection);
      }
      if (nextSingleStationReciprocatingShuttleCollection) {
        disposeDxfSingleStationReciprocatingShuttleCollection(
          nextSingleStationReciprocatingShuttleCollection
        );
      }
      nextContext.dispose();
      if (!disposed) {
        renderContext = createCargoRenderContext(
          previousOptions,
          scene,
          renderer,
          currentTarget,
          modelAssetLibrary,
          deviceModelLibrary
        );
      }
      throw error;
    } finally {
      isUpdatingRack = false;
    }

    if (disposed) {
      disposeDxfRackCollection(nextRackCollection);
      disposeDxfDeviceCollection(nextDeviceCollection);
      disposeDxfDoubleChainConveyorCollection(nextDoubleChainConveyorCollection);
      disposeDxfRollerConveyorCollection(nextRollerConveyorCollection);
      disposeDxfRollerLiftTransferCollection(nextRollerLiftTransferCollection);
      disposeDxfPalletReciprocatingLiftCollection(nextPalletReciprocatingLiftCollection);
      disposeDxfSingleStationReciprocatingShuttleCollection(
        nextSingleStationReciprocatingShuttleCollection
      );
      disposeDxfPalletStackerCraneCollection(nextPalletStackerCraneCollection);
      nextContext.dispose();
      return;
    }

    const nextContentGroup = new Group();
    nextContentGroup.name = "dxf-scene-content";
    nextContentGroup.add(
      nextRackCollection.group,
      nextDeviceCollection.group,
      nextDoubleChainConveyorCollection.group,
      nextRollerConveyorCollection.group,
      nextRollerLiftTransferCollection.group,
      nextPalletReciprocatingLiftCollection.group,
      nextSingleStationReciprocatingShuttleCollection.group,
      nextPalletStackerCraneCollection.group
    );
    clearHover();
    selectedRack?.shelf.setSelected(false);
    selectedDevice?.device.setSelected(false);
    selectedPalletReciprocatingLift?.lift.setSelected(false);
    selectedSingleStationReciprocatingShuttle?.shuttle.setSelected(false);
    selectedPalletStackerCrane?.stackerCrane.setSelected(false);
    setBatchSelectionHighlight(selectedBatch, false);
    previousContentGroup.removeFromParent();
    scene.add(nextContentGroup);
    dxfRackCollection = nextRackCollection;
    dxfDeviceCollection = nextDeviceCollection;
    dxfDoubleChainConveyorCollection = nextDoubleChainConveyorCollection;
    dxfRollerConveyorCollection = nextRollerConveyorCollection;
    dxfRollerLiftTransferCollection = nextRollerLiftTransferCollection;
    dxfPalletReciprocatingLiftCollection = nextPalletReciprocatingLiftCollection;
    dxfSingleStationReciprocatingShuttleCollection =
      nextSingleStationReciprocatingShuttleCollection;
    dxfPalletStackerCraneCollection = nextPalletStackerCraneCollection;
    dxfContentGroup = nextContentGroup;
    activeRenderOptions = requestedOptions;
    renderContext = nextContext;
    disposeDxfRackCollection(previousRackCollection);
    disposeDxfDeviceCollection(previousDeviceCollection);
    disposeDxfDoubleChainConveyorCollection(previousDoubleChainConveyorCollection);
    disposeDxfRollerConveyorCollection(previousRollerConveyorCollection);
    disposeDxfRollerLiftTransferCollection(previousRollerLiftTransferCollection);
    disposeDxfPalletReciprocatingLiftCollection(previousPalletReciprocatingLiftCollection);
    disposeDxfSingleStationReciprocatingShuttleCollection(
      previousSingleStationReciprocatingShuttleCollection
    );
    disposeDxfPalletStackerCraneCollection(previousPalletStackerCraneCollection);
    if (restoreBatchSelection(selectedBatchIdentity)) {
      selectedRack = undefined;
      selectedCell = undefined;
      selectedDevice = undefined;
      selectedPalletReciprocatingLift = undefined;
      selectedSingleStationReciprocatingShuttle = undefined;
      selectedPalletStackerCrane = undefined;
    } else if (selectedPalletStackerCraneIdentity) {
      restorePalletStackerCraneSelection(
        selectedPalletStackerCraneIdentity,
        nextPalletStackerCraneCollection
      );
    } else if (selectedSingleStationReciprocatingShuttleIdentity) {
      restoreSingleStationReciprocatingShuttleSelection(
        selectedSingleStationReciprocatingShuttleIdentity,
        nextSingleStationReciprocatingShuttleCollection
      );
    } else if (selectedPalletReciprocatingLiftIdentity) {
      restorePalletReciprocatingLiftSelection(
        selectedPalletReciprocatingLiftIdentity,
        nextPalletReciprocatingLiftCollection
      );
    } else if (selectedDeviceIdentity) {
      restoreDeviceSelection(selectedDeviceIdentity, nextDeviceCollection);
    } else {
      restoreSelection(selectedIdentity, nextRackCollection);
    }
    nextContext.refit(nextContentGroup);
    requestRender();
  };

  return {
    captureCameraState() {
      return {
        position: camera.position.toArray(),
        target: controls.target.toArray()
      };
    },
    getBatchSettingRegions,
    getRackCargoUpdatesForSelection(rackIds, asset) {
      const racks = rackIds
        .map((rackId) => dxfRackCollection?.racks.get(rackId))
        .filter((rack): rack is DxfRackInstance => Boolean(rack));
      if (
        racks.length !== rackIds.length ||
        racks.length === 0 ||
        (asset && racks.some((rack) => !isCargoAssetAllowed(rack.kind, asset)))
      ) {
        return;
      }
      return new Map(
        racks.map((rack) => [
          rack.id,
          createRackCargoPlacements(rack).map(({ cellId, heightIndex, slotIndex }) => ({
            asset,
            cellId,
            heightIndex,
            slotIndex
          }))
        ])
      );
    },
    setMultiSelectionMode(active) {
      updateMultiSelectionMode(active);
    },
    clearDxfReference() {
      dxfLoadGeneration += 1;
      dxfLoadAbortController?.abort();
      dxfLoadAbortController = undefined;
      clearHover();
      clearSelection();
      dxfReference?.dispose();
      dxfReference = undefined;
      activeDxfFile = undefined;
      if (
        dxfContentGroup &&
        dxfRackCollection &&
        dxfDeviceCollection &&
        dxfDoubleChainConveyorCollection &&
        dxfRollerConveyorCollection &&
        dxfRollerLiftTransferCollection &&
        dxfPalletReciprocatingLiftCollection &&
        dxfSingleStationReciprocatingShuttleCollection &&
        dxfPalletStackerCraneCollection
      ) {
        releaseDxfContent(
          dxfContentGroup,
          dxfRackCollection,
          dxfDeviceCollection,
          dxfDoubleChainConveyorCollection,
          dxfRollerConveyorCollection,
          dxfRollerLiftTransferCollection,
          dxfPalletReciprocatingLiftCollection,
          dxfSingleStationReciprocatingShuttleCollection,
          dxfPalletStackerCraneCollection
        );
      }
      dxfContentGroup = undefined;
      dxfRackCollection = undefined;
      dxfDeviceCollection = undefined;
      dxfDoubleChainConveyorCollection = undefined;
      dxfRollerConveyorCollection = undefined;
      dxfRollerLiftTransferCollection = undefined;
      dxfPalletReciprocatingLiftCollection = undefined;
      dxfSingleStationReciprocatingShuttleCollection = undefined;
      dxfPalletStackerCraneCollection = undefined;
      grid.visible = true;
      renderContext.refit(grid);
      resetCamera(camera, controls);
      viewHelper.center.copy(controls.target);
      requestRender();
    },
    exportSceneGlb() {
      return enqueueUpdate(async () => {
        if (disposed) {
          throw new Error("场景已经关闭，无法导出");
        }
        if (!dxfContentGroup) {
          throw new Error("当前场景没有可导出的三维模型");
        }
        const restoreHighlights = hideInteractionHighlights();
        try {
          return await exportCargoSceneGlb(dxfContentGroup);
        } finally {
          restoreHighlights();
        }
      });
    },
    renderSceneImage({ includeCad }) {
      if (cameraLocked) {
        return Promise.reject(new Error("场景图片正在生成"));
      }
      const unlockCamera = lockCamera();
      const imagePromise = enqueueUpdate(async () => {
        if (disposed) {
          throw new Error("场景已经关闭，无法生成图片");
        }
        if (!dxfReference || !dxfContentGroup) {
          throw new Error("当前场景没有可生成图片的三维模型");
        }

        const previousOptions = { ...activeRenderOptions };
        try {
          await applyRenderOptions(highQualityCargoRenderOptions);

          const activeReference = dxfReference;
          const referenceWasVisible = activeReference.mesh.visible;
          const axesWereVisible = axes.visible;
          const axisLabelVisibility = axisLabels.map((label) => label.visible);
          const restoreHighlights = hideInteractionHighlights();

          activeReference.mesh.visible = includeCad;
          axes.visible = false;
          axisLabels.forEach((label) => {
            label.visible = false;
          });

          try {
            return await renderCargoSceneImage(renderer, scene, camera);
          } finally {
            activeReference.mesh.visible = referenceWasVisible;
            axes.visible = axesWereVisible;
            axisLabels.forEach((label, index) => {
              label.visible = axisLabelVisibility[index] ?? true;
            });
            restoreHighlights();
          }
        } finally {
          await applyRenderOptions(previousOptions);
        }
      });
      return imagePromise.finally(unlockCamera);
    },
    async loadDxfReference(file, onStage, projectState) {
      const generation = dxfLoadGeneration + 1;
      dxfLoadGeneration = generation;
      dxfLoadAbortController?.abort();
      const abortController = new AbortController();
      dxfLoadAbortController = abortController;

      try {
        const nextReference = await createDxfFloorReference(file, {
          anisotropy: renderer.capabilities.getMaxAnisotropy(),
          deviceBlockPrefixes: getDeviceBlockPrefixes(activeDeviceModels),
          maxTextureSize: renderer.capabilities.maxTextureSize,
          onStage,
          signal: abortController.signal
        });

        let nextRackCollection: DxfRackCollection | undefined;
        let nextDeviceCollection: DxfDeviceCollection | undefined;
        let nextDoubleChainConveyorCollection: DxfDoubleChainConveyorCollection | undefined;
        let nextRollerConveyorCollection: DxfRollerConveyorCollection | undefined;
        let nextRollerLiftTransferCollection: DxfRollerLiftTransferCollection | undefined;
        let nextPalletReciprocatingLiftCollection: DxfPalletReciprocatingLiftCollection | undefined;
        let nextSingleStationReciprocatingShuttleCollection:
          | DxfSingleStationReciprocatingShuttleCollection
          | undefined;
        let nextPalletStackerCraneCollection: DxfPalletStackerCraneCollection | undefined;
        try {
          nextRackCollection = await createDxfRackCollection(
            nextReference.rackPlacements,
            nextReference.binRackPlacements,
            renderContext.modelAssetLoader,
            renderContext.maxCargoPrimitiveTriangles,
            renderContext.prepareObject,
            createProjectRackSnapshots(projectState)
          );
          nextDeviceCollection = await createDxfDeviceCollection(
            nextReference.devicePlacements,
            activeDeviceModels,
            renderContext.deviceModelLoader,
            new Map(Object.entries(projectState?.devices ?? {}))
          );
          nextDoubleChainConveyorCollection = await createDxfDoubleChainConveyorCollection(
            nextReference.doubleChainConveyorPlacements,
            renderContext.modelAssetLoader,
            renderContext.prepareObject
          );
          nextRollerConveyorCollection = await createDxfRollerConveyorCollection(
            nextReference.rollerConveyorPlacements,
            renderContext.modelAssetLoader,
            renderContext.prepareObject
          );
          nextRollerLiftTransferCollection = await createDxfRollerLiftTransferCollection(
            nextReference.rollerLiftTransferPlacements,
            renderContext.modelAssetLoader,
            renderContext.prepareObject
          );
          nextPalletReciprocatingLiftCollection = await createDxfPalletReciprocatingLiftCollection(
            nextReference.palletReciprocatingLiftPlacements,
            renderContext.modelAssetLoader,
            renderContext.prepareObject,
            new Map(Object.entries(projectState?.lifts ?? {}))
          );
          nextSingleStationReciprocatingShuttleCollection =
            await createDxfSingleStationReciprocatingShuttleCollection(
              nextReference.singleStationReciprocatingShuttlePlacements,
              renderContext.modelAssetLoader,
              renderContext.prepareObject,
              new Map(Object.entries(projectState?.singleStationReciprocatingShuttles ?? {}))
            );
          nextPalletStackerCraneCollection = await createDxfPalletStackerCraneCollection(
            nextReference.palletStackerCranePlacements,
            renderContext.modelAssetLoader,
            renderContext.prepareObject,
            new Map(Object.entries(projectState?.palletStackerCranes ?? {}))
          );
        } catch (error) {
          nextReference.dispose();
          if (nextRackCollection) {
            disposeDxfRackCollection(nextRackCollection);
          }
          if (nextDeviceCollection) {
            disposeDxfDeviceCollection(nextDeviceCollection);
          }
          if (nextDoubleChainConveyorCollection) {
            disposeDxfDoubleChainConveyorCollection(nextDoubleChainConveyorCollection);
          }
          if (nextRollerConveyorCollection) {
            disposeDxfRollerConveyorCollection(nextRollerConveyorCollection);
          }
          if (nextRollerLiftTransferCollection) {
            disposeDxfRollerLiftTransferCollection(nextRollerLiftTransferCollection);
          }
          if (nextPalletReciprocatingLiftCollection) {
            disposeDxfPalletReciprocatingLiftCollection(nextPalletReciprocatingLiftCollection);
          }
          if (nextPalletStackerCraneCollection) {
            disposeDxfPalletStackerCraneCollection(nextPalletStackerCraneCollection);
          }
          if (nextSingleStationReciprocatingShuttleCollection) {
            disposeDxfSingleStationReciprocatingShuttleCollection(
              nextSingleStationReciprocatingShuttleCollection
            );
          }
          throw error;
        }

        const nextContentGroup = new Group();
        nextContentGroup.name = "dxf-scene-content";
        nextContentGroup.add(
          nextRackCollection.group,
          nextDeviceCollection.group,
          nextDoubleChainConveyorCollection.group,
          nextRollerConveyorCollection.group,
          nextRollerLiftTransferCollection.group,
          nextPalletReciprocatingLiftCollection.group,
          nextSingleStationReciprocatingShuttleCollection.group,
          nextPalletStackerCraneCollection.group
        );

        if (disposed || generation !== dxfLoadGeneration) {
          nextReference.dispose();
          disposeDxfRackCollection(nextRackCollection);
          disposeDxfDeviceCollection(nextDeviceCollection);
          disposeDxfDoubleChainConveyorCollection(nextDoubleChainConveyorCollection);
          disposeDxfRollerConveyorCollection(nextRollerConveyorCollection);
          disposeDxfRollerLiftTransferCollection(nextRollerLiftTransferCollection);
          disposeDxfPalletReciprocatingLiftCollection(nextPalletReciprocatingLiftCollection);
          disposeDxfSingleStationReciprocatingShuttleCollection(
            nextSingleStationReciprocatingShuttleCollection
          );
          disposeDxfPalletStackerCraneCollection(nextPalletStackerCraneCollection);
          throw new DOMException("DXF 图纸加载已取消", "AbortError");
        }

        const previousReference = dxfReference;
        const previousRackCollection = dxfRackCollection;
        const previousDeviceCollection = dxfDeviceCollection;
        const previousDoubleChainConveyorCollection = dxfDoubleChainConveyorCollection;
        const previousRollerConveyorCollection = dxfRollerConveyorCollection;
        const previousRollerLiftTransferCollection = dxfRollerLiftTransferCollection;
        const previousPalletReciprocatingLiftCollection = dxfPalletReciprocatingLiftCollection;
        const previousSingleStationReciprocatingShuttleCollection =
          dxfSingleStationReciprocatingShuttleCollection;
        const previousPalletStackerCraneCollection = dxfPalletStackerCraneCollection;
        const previousContentGroup = dxfContentGroup;
        clearHover();
        clearSelection();
        dxfReference = nextReference;
        activeDxfFile = file;
        dxfRackCollection = nextRackCollection;
        dxfDeviceCollection = nextDeviceCollection;
        dxfDoubleChainConveyorCollection = nextDoubleChainConveyorCollection;
        dxfRollerConveyorCollection = nextRollerConveyorCollection;
        dxfRollerLiftTransferCollection = nextRollerLiftTransferCollection;
        dxfPalletReciprocatingLiftCollection = nextPalletReciprocatingLiftCollection;
        dxfSingleStationReciprocatingShuttleCollection =
          nextSingleStationReciprocatingShuttleCollection;
        dxfPalletStackerCraneCollection = nextPalletStackerCraneCollection;
        dxfContentGroup = nextContentGroup;
        scene.add(nextReference.mesh, nextContentGroup);
        grid.visible = false;
        previousReference?.dispose();
        if (
          previousContentGroup &&
          previousRackCollection &&
          previousDeviceCollection &&
          previousDoubleChainConveyorCollection &&
          previousRollerConveyorCollection &&
          previousRollerLiftTransferCollection &&
          previousPalletReciprocatingLiftCollection &&
          previousPalletStackerCraneCollection &&
          previousSingleStationReciprocatingShuttleCollection
        ) {
          releaseDxfContent(
            previousContentGroup,
            previousRackCollection,
            previousDeviceCollection,
            previousDoubleChainConveyorCollection,
            previousRollerConveyorCollection,
            previousRollerLiftTransferCollection,
            previousPalletReciprocatingLiftCollection,
            previousSingleStationReciprocatingShuttleCollection,
            previousPalletStackerCraneCollection
          );
        }
        renderContext.refit(nextContentGroup);
        frameCameraToObject(
          camera,
          controls,
          nextReference.rackPlacements.length > 0 ||
            nextReference.binRackPlacements.length > 0 ||
            nextReference.devicePlacements.length > 0 ||
            nextReference.doubleChainConveyorPlacements.length > 0 ||
            nextReference.rollerConveyorPlacements.length > 0 ||
            nextReference.rollerLiftTransferPlacements.length > 0 ||
            nextReference.palletReciprocatingLiftPlacements.length > 0 ||
            nextReference.palletStackerCranePlacements.length > 0 ||
            nextReference.singleStationReciprocatingShuttlePlacements.length > 0
            ? nextContentGroup
            : nextReference.mesh,
          host.clientWidth,
          host.clientHeight
        );
        viewHelper.center.copy(controls.target);
        requestRender();
        return nextReference.metadata;
      } finally {
        if (dxfLoadAbortController === abortController) {
          dxfLoadAbortController = undefined;
        }
      }
    },
    async setDeviceModel(deviceId, modelId) {
      const instance = dxfDeviceCollection?.devices.get(deviceId);
      const model = activeDeviceModels.find(
        (candidate) => candidate.id === modelId && candidate.blockPrefix === instance?.blockPrefix
      );
      if (!instance || !model || instance.device.getModelId() === model.id) {
        return;
      }

      const generation = (deviceModelRequestGenerations.get(deviceId) ?? 0) + 1;
      deviceModelRequestGenerations.set(deviceId, generation);
      const context = renderContext;
      const content = await context.deviceModelLoader.load(model);

      return enqueueUpdate(async () => {
        if (
          disposed ||
          !isActiveDevice(instance) ||
          renderContext !== context ||
          deviceModelRequestGenerations.get(deviceId) !== generation
        ) {
          disposeRenderableObject(content);
          return;
        }

        instance.device.setModel(model, content);
        if (selectedDevice === instance) {
          instance.device.setSelected(true);
          options.onSelectionChange(createDeviceSelection(instance, activeDeviceModels));
        }
        requestRender();
      });
    },
    async setDeviceModelsForSelection(deviceIds, modelId) {
      const instances = deviceIds
        .map((deviceId) => dxfDeviceCollection?.devices.get(deviceId))
        .filter((device): device is DxfDeviceInstance => Boolean(device));
      const blockPrefix = instances[0]?.blockPrefix;
      const model = activeDeviceModels.find(
        (candidate) => candidate.id === modelId && candidate.blockPrefix === blockPrefix
      );
      if (
        instances.length !== deviceIds.length ||
        instances.length === 0 ||
        !model ||
        instances.some((instance) => instance.blockPrefix !== blockPrefix)
      ) {
        return;
      }

      const context = renderContext;
      const contents: Object3D[] = [];
      try {
        for (const _instance of instances) {
          contents.push(await context.deviceModelLoader.load(model));
        }
      } catch (error) {
        contents.forEach(disposeRenderableObject);
        throw error;
      }
      return enqueueUpdate(async () => {
        if (
          disposed ||
          renderContext !== context ||
          instances.some((instance) => !isActiveDevice(instance))
        ) {
          contents.forEach(disposeRenderableObject);
          return;
        }
        instances.forEach((instance, index) => {
          const content = contents[index];
          if (content) {
            instance.device.setModel(model, content);
          }
        });
        if (
          selectedBatch?.kind === "device-batch" &&
          haveSameIds(selectedBatch.deviceIds, deviceIds)
        ) {
          selectedBatch = { ...selectedBatch, modelId, modelMixed: false };
          setBatchSelectionHighlight(selectedBatch, true);
          options.onSelectionChange(selectedBatch);
        }
        requestRender();
      });
    },
    setDeviceModels(models) {
      const requestedModels = [...models];
      return enqueueUpdate(async () => {
        if (disposed || areDeviceModelsEqual(activeDeviceModels, requestedModels)) {
          return;
        }

        const previousModels = activeDeviceModels;
        const reference = dxfReference;
        const file = activeDxfFile;
        const previousCollection = dxfDeviceCollection;
        const contentGroup = dxfContentGroup;
        activeDeviceModels = requestedModels;

        if (!reference || !file || !previousCollection || !contentGroup) {
          return;
        }

        const snapshots = captureDxfDeviceSnapshots(previousCollection);
        const selectedBatchIdentity =
          selectedBatch?.kind === "device-batch" ? selectedBatch : undefined;
        const selectedIdentity = selectedDevice
          ? { deviceId: selectedDevice.id, modelId: selectedDevice.device.getModelId() }
          : undefined;
        let placements = reference.devicePlacements;
        let parsedReference: DxfFloorReference | undefined;
        let nextCollection: DxfDeviceCollection | undefined;

        try {
          if (!areDeviceBlockPrefixesEqual(previousModels, requestedModels)) {
            parsedReference = await createDxfFloorReference(file, {
              anisotropy: renderer.capabilities.getMaxAnisotropy(),
              deviceBlockPrefixes: getDeviceBlockPrefixes(requestedModels),
              maxTextureSize: renderer.capabilities.maxTextureSize
            });
            placements = parsedReference.devicePlacements;
          }
          nextCollection = await createDxfDeviceCollection(
            placements,
            requestedModels,
            renderContext.deviceModelLoader,
            snapshots
          );
        } catch (error) {
          activeDeviceModels = previousModels;
          if (nextCollection) {
            disposeDxfDeviceCollection(nextCollection);
          }
          throw error;
        } finally {
          parsedReference?.dispose();
        }

        if (disposed) {
          disposeDxfDeviceCollection(nextCollection);
          return;
        }

        selectedDevice?.device.setSelected(false);
        setBatchSelectionHighlight(selectedBatchIdentity, false);
        previousCollection.group.removeFromParent();
        contentGroup.add(nextCollection.group);
        dxfDeviceCollection = nextCollection;
        reference.devicePlacements = placements;
        disposeDxfDeviceCollection(previousCollection);
        if (restoreBatchSelection(selectedBatchIdentity)) {
          selectedDevice = undefined;
        } else if (selectedIdentity) {
          restoreDeviceSelection(selectedIdentity, nextCollection);
        }
        renderContext.refit(contentGroup);
        requestRender();
      });
    },
    setBrowseCameraEditing(editing) {
      if (!browseModeActive || cameraLocked || browseCameraEditing === editing) {
        return;
      }
      cameraTransition = undefined;
      browseCameraEditing = editing;
      if (!editing) {
        viewHelper.animating = false;
      }
      setControlsEnabled(editing);
      requestRender();
    },
    setBrowseMode(active) {
      if (cameraLocked || browseModeActive === active) {
        return;
      }
      cameraTransition = undefined;
      browseModeActive = active;
      browseCameraEditing = false;
      clearHover();
      updateMultiSelectionMode(false);
      clearSelection();
      viewHelper.animating = false;
      setControlsEnabled(!active);
      axes.visible = !active;
      axisLabels.forEach((label) => {
        label.visible = !active;
      });
      requestRender();
    },
    setCameraState(state) {
      if (!browseModeActive || browseCameraEditing || cameraLocked) {
        return;
      }
      applyCameraState(state);
    },
    setCargo(selection, slotIndex, asset) {
      const rack = dxfRackCollection?.racks.get(selection.rackId);
      if (!rack) {
        return Promise.resolve();
      }

      return enqueueUpdate(async () => {
        if (disposed || !isActiveRack(rack)) {
          return;
        }

        isUpdatingRack = true;
        const placement = {
          cellId: selection.cellId,
          cellIndex: selection.cellIndex,
          heightIndex: selection.heightIndex,
          slotIndex
        };

        try {
          if (!asset) {
            rack.shelf.removeCargo(placement);
            rack.cargoAssets.delete(createCargoAssetKey(placement));
            requestRender();
            return;
          }

          if (!isCargoAssetAllowed(rack.kind, asset)) {
            throw new RangeError(`Cargo asset ${asset} is not allowed for ${rack.kind} racks`);
          }

          if (
            asset === ModelAsset.BinCargo &&
            rack.shelf instanceof BinRack &&
            rack.shelf.restoreInstancedCargo(placement)
          ) {
            rack.cargoAssets.set(createCargoAssetKey(placement), asset);
            requestRender();
            return;
          }

          const cargo = await renderContext.modelAssetLoader.load(asset);
          if (disposed || !isActiveRack(rack)) {
            disposeRenderableObject(cargo);
            return;
          }
          rack.shelf.setCargo(placement, cargo);
          rack.cargoAssets.set(createCargoAssetKey(placement), asset);
          requestRender();
        } finally {
          isUpdatingRack = false;
        }
      });
    },
    setRackCargo(rackId, asset) {
      const rack = dxfRackCollection?.racks.get(rackId);
      if (!rack) {
        return Promise.resolve(undefined);
      }

      return enqueueUpdate(async () => {
        if (disposed || !isActiveRack(rack)) {
          return;
        }
        if (asset && !isCargoAssetAllowed(rack.kind, asset)) {
          throw new RangeError(`Cargo asset ${asset} is not allowed for ${rack.kind} racks`);
        }

        isUpdatingRack = true;
        const placements = createRackCargoPlacements(rack);
        const cargoObjects: Object3D[] = [];
        try {
          if (asset && rack.shelf instanceof PalletRack) {
            for (const _placement of placements) {
              cargoObjects.push(await renderContext.modelAssetLoader.load(asset));
            }
            if (disposed || !isActiveRack(rack)) {
              cargoObjects.forEach(disposeRenderableObject);
              return;
            }
          }

          if (!asset) {
            placements.forEach((placement) => {
              rack.shelf.removeCargo(placement);
            });
          } else if (rack.shelf instanceof BinRack) {
            const template = dxfRackCollection?.sharedCargoTemplate;
            if (!template) {
              throw new Error("料箱货物模型尚未加载");
            }
            rack.shelf.fillCargo(template, {
              maxPrimitiveTriangles: renderContext.maxCargoPrimitiveTriangles
            });
          } else {
            placements.forEach((placement, index) => {
              const cargo = cargoObjects[index];
              if (cargo) {
                rack.shelf.setCargo(placement, cargo);
              }
            });
          }

          rack.cargoAssets.clear();
          if (asset) {
            placements.forEach((placement) => {
              rack.cargoAssets.set(createCargoAssetKey(placement), asset);
            });
          }
          refreshDxfMirror(rack.shelf);
          if (selectedRack === rack && selectedCell) {
            options.onSelectionChange(createSelection(rack, selectedCell));
          }
          requestRender();
          return placements.map(({ cellId, heightIndex, slotIndex }) => ({
            asset,
            cellId,
            heightIndex,
            slotIndex
          }));
        } catch (error) {
          cargoObjects.forEach((cargo) => {
            if (!cargo.parent) {
              disposeRenderableObject(cargo);
            }
          });
          throw error;
        } finally {
          isUpdatingRack = false;
        }
      });
    },
    setRackCargoForSelection(rackIds, asset) {
      const racks = rackIds
        .map((rackId) => dxfRackCollection?.racks.get(rackId))
        .filter((rack): rack is DxfRackInstance => Boolean(rack));
      if (racks.length !== rackIds.length || racks.length === 0) {
        return Promise.resolve(undefined);
      }

      return enqueueUpdate(async () => {
        if (disposed || racks.some((rack) => !isActiveRack(rack))) {
          return;
        }
        if (asset && racks.some((rack) => !isCargoAssetAllowed(rack.kind, asset))) {
          throw new RangeError(`Cargo asset ${asset} is not allowed for the selected racks`);
        }
        if (
          asset === ModelAsset.BinCargo &&
          racks.some((rack) => rack.shelf instanceof BinRack) &&
          !dxfRackCollection?.sharedCargoTemplate
        ) {
          throw new Error("料箱货物模型尚未加载");
        }

        isUpdatingRack = true;
        try {
          const updates = new Map<string, CargoEditorRackCargoUpdate[]>();
          for (const rack of racks) {
            const placements = createRackCargoPlacements(rack);
            const cargoObjects: Object3D[] = [];
            if (asset && rack.shelf instanceof PalletRack) {
              try {
                for (const _placement of placements) {
                  cargoObjects.push(await renderContext.modelAssetLoader.load(asset));
                }
              } catch (error) {
                cargoObjects.forEach(disposeRenderableObject);
                throw error;
              }
              if (disposed || !isActiveRack(rack)) {
                cargoObjects.forEach(disposeRenderableObject);
                return updates.size > 0 ? updates : undefined;
              }
            }

            if (!asset) {
              placements.forEach((placement) => {
                rack.shelf.removeCargo(placement);
              });
            } else if (rack.shelf instanceof BinRack) {
              const template = dxfRackCollection?.sharedCargoTemplate;
              if (!template) {
                return;
              }
              rack.shelf.fillCargo(template, {
                maxPrimitiveTriangles: renderContext.maxCargoPrimitiveTriangles
              });
            } else {
              placements.forEach((placement, index) => {
                const cargo = cargoObjects[index];
                if (cargo) {
                  rack.shelf.setCargo(placement, cargo);
                }
              });
            }
            rack.cargoAssets.clear();
            if (asset) {
              placements.forEach((placement) => {
                rack.cargoAssets.set(createCargoAssetKey(placement), asset);
              });
            }
            refreshDxfMirror(rack.shelf);
            updates.set(
              rack.id,
              placements.map(({ cellId, heightIndex, slotIndex }) => ({
                asset,
                cellId,
                heightIndex,
                slotIndex
              }))
            );
            requestRender();
            await yieldToMainThread();
          }
          return updates;
        } finally {
          isUpdatingRack = false;
        }
      });
    },
    setRackCargoCapacityForSelection(rackIds, cargoCapacity) {
      const racks = rackIds
        .map((rackId) => dxfRackCollection?.racks.get(rackId))
        .filter((rack): rack is DxfRackInstance => Boolean(rack));
      if (
        racks.length !== rackIds.length ||
        racks.length === 0 ||
        !Number.isInteger(cargoCapacity) ||
        cargoCapacity <= 0
      ) {
        return Promise.resolve(undefined);
      }
      return enqueueUpdate(async () => {
        if (disposed || racks.some((rack) => !isActiveRack(rack))) {
          return;
        }
        isUpdatingRack = true;
        try {
          racks.forEach((rack) => {
            rack.cells.forEach((cell) => {
              rack.shelf.setCargoCapacity(cell.id, cargoCapacity);
            });
            pruneRackCargoAssetsForLimits(
              rack,
              cargoCapacity,
              rack.shelf.getConfiguration().layerHeights.length
            );
            refreshDxfMirror(rack.shelf);
          });
          if (selectedBatch?.kind === "rack-batch" && haveSameIds(selectedBatch.rackIds, rackIds)) {
            selectedBatch = { ...selectedBatch, cargoCapacity, cargoCapacityMixed: false };
            setBatchSelectionHighlight(selectedBatch, true);
            options.onSelectionChange(selectedBatch);
          }
          if (dxfContentGroup) {
            renderContext.refit(dxfContentGroup);
          }
          requestRender();
          return new Map(racks.map((rack) => [rack.id, rack.cells.map(({ id }) => id)]));
        } finally {
          isUpdatingRack = false;
        }
      });
    },
    setRackLayerHeightsForSelection(rackIds, layerHeights) {
      const racks = rackIds
        .map((rackId) => dxfRackCollection?.racks.get(rackId))
        .filter((rack): rack is DxfRackInstance => Boolean(rack));
      if (
        racks.length !== rackIds.length ||
        racks.length === 0 ||
        layerHeights.length === 0 ||
        layerHeights.some((height) => !Number.isFinite(height) || height <= 0)
      ) {
        return Promise.resolve();
      }
      return enqueueUpdate(async () => {
        if (disposed || racks.some((rack) => !isActiveRack(rack))) {
          return;
        }
        isUpdatingRack = true;
        const previousLayerHeights = new Map(
          racks.map((rack) => [rack, rack.shelf.getConfiguration().layerHeights] as const)
        );
        const previousCargoAssets = new Map(
          racks.map((rack) => [rack, new Map(rack.cargoAssets)] as const)
        );
        try {
          for (const rack of racks) {
            await rack.shelf.setLayerHeights(layerHeights, renderContext.modelAssetLoader);
            pruneRackCargoAssetsForLimits(rack, undefined, layerHeights.length);
            refreshDxfMirror(rack.shelf);
          }
        } catch (error) {
          for (const rack of racks) {
            const previous = previousLayerHeights.get(rack);
            if (previous) {
              await rack.shelf.setLayerHeights(previous, renderContext.modelAssetLoader);
              rack.cargoAssets.clear();
              await applyRackCargoOverrides(
                rack.shelf,
                rack.cells,
                rack.cargoAssets,
                {
                  cargoAssets: new Map(previousCargoAssets.get(rack)),
                  cargoCapacities: new Map(),
                  layerHeights: previous
                },
                renderContext.modelAssetLoader
              );
              refreshDxfMirror(rack.shelf);
            }
          }
          throw error;
        } finally {
          isUpdatingRack = false;
        }
        if (selectedBatch?.kind === "rack-batch" && haveSameIds(selectedBatch.rackIds, rackIds)) {
          selectedBatch = {
            ...selectedBatch,
            layerHeights: [...layerHeights],
            layerHeightsMixed: false
          };
          setBatchSelectionHighlight(selectedBatch, true);
          options.onSelectionChange(selectedBatch);
        }
        if (dxfContentGroup) {
          renderContext.refit(dxfContentGroup);
        }
        requestRender();
      });
    },
    setRackConfiguration(rackId, cellId, nextConfiguration) {
      const rack = dxfRackCollection?.racks.get(rackId);
      if (!rack) {
        return Promise.resolve();
      }

      return enqueueUpdate(async () => {
        if (disposed || !isActiveRack(rack)) {
          return;
        }

        isUpdatingRack = true;
        try {
          clearHover();
          rack.shelf.clearCargoCellSelection();
          rack.shelf.setCargoCapacity(cellId, nextConfiguration.cargoCapacity);
          await rack.shelf.setLayerHeights(
            nextConfiguration.layerHeights,
            renderContext.modelAssetLoader
          );
          refreshDxfMirror(rack.shelf);
          pruneRackCargoAssets(rack, cellId, nextConfiguration);
          if (disposed || !isActiveRack(rack)) {
            return;
          }

          if (dxfContentGroup) {
            renderContext.refit(dxfContentGroup);
          }
          if (
            selectedRack === rack &&
            selectedCell &&
            selectedCell.heightIndex < nextConfiguration.layerHeights.length
          ) {
            const target = rack.shelf.getCargoCellHitTarget(selectedCell);
            if (target) {
              rack.shelf.selectCargoCell(target);
              options.onSelectionChange(createSelection(rack, selectedCell));
            }
          } else if (selectedRack === rack) {
            clearSelection();
          }
          requestRender();
        } finally {
          isUpdatingRack = false;
        }
      });
    },
    setPalletReciprocatingLiftLayerCount(liftId, layerCount) {
      const instance = dxfPalletReciprocatingLiftCollection?.lifts.get(liftId);
      if (!instance || disposed) {
        return;
      }

      instance.lift.setLayerCount(layerCount);
      refreshDxfMirror(instance.lift);
      if (selectedPalletReciprocatingLift === instance) {
        options.onSelectionChange(createPalletReciprocatingLiftSelection(instance));
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setPalletReciprocatingLiftLayerCountForSelection(liftIds, layerCount) {
      const instances = liftIds
        .map((liftId) => dxfPalletReciprocatingLiftCollection?.lifts.get(liftId))
        .filter((lift): lift is DxfPalletReciprocatingLiftInstance => Boolean(lift));
      if (disposed || instances.length !== liftIds.length || instances.length === 0) {
        return;
      }
      instances.forEach(({ lift }) => {
        lift.setLayerCount(layerCount);
        refreshDxfMirror(lift);
      });
      if (
        selectedBatch?.kind === "pallet-reciprocating-lift-batch" &&
        haveSameIds(selectedBatch.liftIds, liftIds)
      ) {
        selectedBatch = { ...selectedBatch, layerCount, layerCountMixed: false };
        setBatchSelectionHighlight(selectedBatch, true);
        options.onSelectionChange(selectedBatch);
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setSingleStationReciprocatingShuttleHeight(shuttleId, height) {
      const instance = dxfSingleStationReciprocatingShuttleCollection?.shuttles.get(shuttleId);
      if (!instance || disposed) {
        return;
      }

      instance.shuttle.setHeight(height);
      refreshDxfMirror(instance.shuttle);
      if (selectedSingleStationReciprocatingShuttle === instance) {
        options.onSelectionChange(createSingleStationReciprocatingShuttleSelection(instance));
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setPalletStackerCraneHeight(stackerCraneId, height) {
      const instance = dxfPalletStackerCraneCollection?.stackerCranes.get(stackerCraneId);
      if (!instance || disposed) {
        return;
      }

      instance.stackerCrane.setHeight(height);
      refreshDxfMirror(instance.stackerCrane);
      if (selectedPalletStackerCrane === instance) {
        options.onSelectionChange(createPalletStackerCraneSelection(instance));
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setSingleStationReciprocatingShuttleHeightForSelection(shuttleIds, height) {
      const instances = shuttleIds
        .map((shuttleId) => dxfSingleStationReciprocatingShuttleCollection?.shuttles.get(shuttleId))
        .filter((shuttle): shuttle is DxfSingleStationReciprocatingShuttleInstance =>
          Boolean(shuttle)
        );
      if (disposed || instances.length !== shuttleIds.length || instances.length === 0) {
        return;
      }
      instances.forEach(({ shuttle }) => {
        shuttle.setHeight(height);
        refreshDxfMirror(shuttle);
      });
      if (
        selectedBatch?.kind === "single-station-reciprocating-shuttle-batch" &&
        haveSameIds(selectedBatch.shuttleIds, shuttleIds)
      ) {
        selectedBatch = { ...selectedBatch, height, heightMixed: false };
        setBatchSelectionHighlight(selectedBatch, true);
        options.onSelectionChange(selectedBatch);
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setPalletStackerCraneHeightForSelection(stackerCraneIds, height) {
      const instances = stackerCraneIds
        .map((stackerCraneId) => dxfPalletStackerCraneCollection?.stackerCranes.get(stackerCraneId))
        .filter((stackerCrane): stackerCrane is DxfPalletStackerCraneInstance =>
          Boolean(stackerCrane)
        );
      if (disposed || instances.length !== stackerCraneIds.length || instances.length === 0) {
        return;
      }
      instances.forEach(({ stackerCrane }) => {
        stackerCrane.setHeight(height);
        refreshDxfMirror(stackerCrane);
      });
      if (
        selectedBatch?.kind === "pallet-stacker-crane-batch" &&
        haveSameIds(selectedBatch.stackerCraneIds, stackerCraneIds)
      ) {
        selectedBatch = { ...selectedBatch, height, heightMixed: false };
        setBatchSelectionHighlight(selectedBatch, true);
        options.onSelectionChange(selectedBatch);
      }
      if (dxfContentGroup) {
        renderContext.refit(dxfContentGroup);
      }
      requestRender();
    },
    setRenderOptions(nextOptions) {
      return enqueueUpdate(() => applyRenderOptions(nextOptions));
    },
    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      dxfLoadGeneration += 1;
      dxfLoadAbortController?.abort();
      dxfLoadAbortController = undefined;
      dxfReference?.dispose();
      dxfReference = undefined;
      activeDxfFile = undefined;
      clearHover();
      const activeRackCollection = dxfRackCollection;
      const activeDeviceCollection = dxfDeviceCollection;
      const activeDoubleChainConveyorCollection = dxfDoubleChainConveyorCollection;
      const activeRollerConveyorCollection = dxfRollerConveyorCollection;
      const activeRollerLiftTransferCollection = dxfRollerLiftTransferCollection;
      const activePalletReciprocatingLiftCollection = dxfPalletReciprocatingLiftCollection;
      const activeSingleStationReciprocatingShuttleCollection =
        dxfSingleStationReciprocatingShuttleCollection;
      const activePalletStackerCraneCollection = dxfPalletStackerCraneCollection;
      const activeContentGroup = dxfContentGroup;
      dxfRackCollection = undefined;
      dxfDeviceCollection = undefined;
      dxfDoubleChainConveyorCollection = undefined;
      dxfRollerConveyorCollection = undefined;
      dxfRollerLiftTransferCollection = undefined;
      dxfPalletReciprocatingLiftCollection = undefined;
      dxfSingleStationReciprocatingShuttleCollection = undefined;
      dxfPalletStackerCraneCollection = undefined;
      dxfContentGroup = undefined;
      activeContentGroup?.removeFromParent();
      resizeObserver.disconnect();
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerdown", handleMultiSelectionPointerDown, true);
      renderer.domElement.removeEventListener("pointerup", handleMultiSelectionPointerUp, true);
      renderer.domElement.removeEventListener(
        "pointercancel",
        handleMultiSelectionPointerCancel,
        true
      );
      renderer.domElement.removeEventListener(
        "lostpointercapture",
        handleMultiSelectionPointerCancel,
        true
      );
      renderer.domElement.removeEventListener("dblclick", handleDoubleClick);
      renderer.domElement.removeEventListener("click", handleClick);
      renderer.domElement.removeEventListener("pointerleave", handlePointerLeave);
      window.removeEventListener("keydown", handleMultiSelectionKeyDown);
      controls.removeEventListener("change", requestRender);
      controls.dispose();
      viewHelper.dispose();
      timer.dispose();
      void updateQueue.finally(() => {
        if (activeRackCollection) {
          disposeDxfRackCollection(activeRackCollection);
        }
        if (activeDeviceCollection) {
          disposeDxfDeviceCollection(activeDeviceCollection);
        }
        if (activeDoubleChainConveyorCollection) {
          disposeDxfDoubleChainConveyorCollection(activeDoubleChainConveyorCollection);
        }
        if (activeRollerConveyorCollection) {
          disposeDxfRollerConveyorCollection(activeRollerConveyorCollection);
        }
        if (activeRollerLiftTransferCollection) {
          disposeDxfRollerLiftTransferCollection(activeRollerLiftTransferCollection);
        }
        if (activePalletReciprocatingLiftCollection) {
          disposeDxfPalletReciprocatingLiftCollection(activePalletReciprocatingLiftCollection);
        }
        if (activePalletStackerCraneCollection) {
          disposeDxfPalletStackerCraneCollection(activePalletStackerCraneCollection);
        }
        if (activeSingleStationReciprocatingShuttleCollection) {
          disposeDxfSingleStationReciprocatingShuttleCollection(
            activeSingleStationReciprocatingShuttleCollection
          );
        }
      });
      grid.geometry.dispose();
      gridMaterials.forEach((material) => {
        material.dispose();
      });
      disposeRenderableObject(axes);
      axisLabels.forEach((label) => {
        label.material.map?.dispose();
        label.material.dispose();
      });
      renderContext.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    }
  };
}

async function createDxfRackCollection(
  palletPlacements: DxfRackPlacement[],
  binPlacements: DxfBinRackPlacementResult[],
  loader: ModelAssetLoader,
  maxCargoPrimitiveTriangles: number | undefined,
  prepareObject: (object: Object3D) => Object3D,
  rackSnapshots: Map<string, DxfRackSnapshot> = new Map()
) {
  const group = new Group();
  const racks = new Map<string, DxfRackInstance>();
  let sharedCargoTemplate: Object3D | undefined;
  group.name = "dxf-racks";

  try {
    for (const placement of palletPlacements) {
      const snapshot = rackSnapshots.get(placement.id);
      const cells = applyRackSnapshotToCells(placement.cells, snapshot);
      const rackShelf = await PalletRack.create(
        {
          cells,
          layerHeightMode: snapshot?.layerHeightMode,
          layerHeights: snapshot?.layerHeights,
          position: {
            x: placement.positionX,
            y: 0,
            z: placement.positionZ
          },
          rotationY: placement.rotationY
        },
        loader
      );
      rackShelf.name = `dxf-pallet-rack-${placement.id.toLowerCase()}`;
      rackShelf.userData.dxfRackId = placement.id;
      prepareObject(rackShelf);
      const cargoAssets = new Map<string, CargoEditorCargoAsset>();
      await applyRackCargoOverrides(rackShelf, cells, cargoAssets, snapshot, loader);
      applyDxfMirror(rackShelf, placement);
      group.add(rackShelf);
      racks.set(placement.id, {
        cargoAssets,
        cells,
        id: placement.id,
        kind: "pallet",
        shelf: rackShelf
      });
    }

    if (binPlacements.length > 0) {
      sharedCargoTemplate = await loader.load(ModelAsset.BinCargo);
    }
    for (const placement of binPlacements) {
      const snapshot = rackSnapshots.get(placement.id);
      const cells = applyRackSnapshotToCells(placement.cells, snapshot);
      const rackShelf = await BinRack.create(
        {
          cells,
          layerHeightMode: snapshot?.layerHeightMode,
          layerHeights: snapshot?.layerHeights,
          position: {
            x: placement.positionX,
            y: 0,
            z: placement.positionZ
          },
          rotationY: placement.rotationY
        },
        loader
      );
      rackShelf.name = `dxf-bin-rack-${placement.id.toLowerCase()}`;
      rackShelf.userData.dxfRackId = placement.id;
      prepareObject(rackShelf);
      const cargoAssets = new Map<string, CargoEditorCargoAsset>();
      if (sharedCargoTemplate) {
        rackShelf.fillCargo(sharedCargoTemplate, {
          maxPrimitiveTriangles: maxCargoPrimitiveTriangles
        });
        const layerCount = rackShelf.getConfiguration().layerHeights.length;
        cells.forEach((cell, cellIndex) => {
          for (let heightIndex = 0; heightIndex < layerCount; heightIndex += 1) {
            for (let slotIndex = 0; slotIndex < cell.cargoCapacity; slotIndex += 1) {
              const cargoPlacement = { cellId: cell.id, cellIndex, heightIndex, slotIndex };
              const key = createCargoAssetKey(cargoPlacement);
              cargoAssets.set(key, ModelAsset.BinCargo);
            }
          }
        });
      }
      await applyRackCargoOverrides(rackShelf, cells, cargoAssets, snapshot, loader);
      applyDxfMirror(rackShelf, placement);
      group.add(rackShelf);
      racks.set(placement.id, {
        cargoAssets,
        cells,
        id: placement.id,
        kind: "bin",
        shelf: rackShelf
      });
    }
  } catch (error) {
    disposeRenderableObject(group);
    if (sharedCargoTemplate) {
      disposeRenderableObject(sharedCargoTemplate);
    }
    throw error;
  }

  return { group, racks, sharedCargoTemplate };
}

function captureDxfRackSnapshots(reference: DxfFloorReference, rackCollection: DxfRackCollection) {
  const snapshots = new Map<string, DxfRackSnapshot>();
  [...reference.rackPlacements, ...reference.binRackPlacements].forEach((placement) => {
    const rack = rackCollection.racks.get(placement.id);
    if (!rack) {
      return;
    }

    snapshots.set(placement.id, {
      cargoAssets: new Map(rack.cargoAssets),
      cargoCapacities: new Map(
        placement.cells.map((cell) => [
          cell.id,
          rack.shelf.getCellConfiguration(cell.id).cargoCapacity
        ])
      ),
      layerHeightMode: "clear",
      layerHeights: rack.shelf.getConfiguration().layerHeights
    });
  });
  return snapshots;
}

function applyRackSnapshotToCells<T extends DxfRackPlacement["cells"]>(
  cells: T,
  snapshot: DxfRackSnapshot | undefined
): T {
  return cells.map((cell) => ({
    ...cell,
    cargoCapacity: snapshot?.cargoCapacities.get(cell.id) ?? cell.cargoCapacity
  })) as T;
}

async function applyRackCargoOverrides(
  rack: BinRack | PalletRack,
  cells: DxfRackPlacement["cells"],
  cargoAssets: Map<string, CargoEditorCargoAsset>,
  snapshot: DxfRackSnapshot | undefined,
  loader: ModelAssetLoader
) {
  for (const [key, asset] of snapshot?.cargoAssets ?? []) {
    const placement = parseCargoAssetKey(key);
    const cellIndex = cells.findIndex((cell) => cell.id === placement.cellId);
    if (cellIndex < 0) {
      continue;
    }
    if (!asset) {
      rack.removeCargo({ ...placement, cellIndex });
      cargoAssets.delete(key);
      continue;
    }
    if (asset === ModelAsset.BinCargo && rack instanceof BinRack) {
      if (!rack.restoreInstancedCargo({ ...placement, cellIndex })) {
        rack.setCargo({ ...placement, cellIndex }, await loader.load(asset));
      }
    } else {
      rack.setCargo({ ...placement, cellIndex }, await loader.load(asset));
    }
    cargoAssets.set(key, asset);
  }
}

function createProjectRackSnapshots(projectState: ProjectSceneState | undefined) {
  return new Map(
    Object.entries(projectState?.racks ?? {}).map(([rackId, state]) => [
      rackId,
      {
        cargoAssets: new Map(
          Object.entries(state.cargo).map(([key, asset]) => [
            key,
            asset ? toCargoEditorAsset(asset) : null
          ])
        ),
        cargoCapacities: new Map(Object.entries(state.cargoCapacities)),
        layerHeightMode:
          state.layerHeights && state.layerHeightMode !== "clear" ? "legacy-span" : "clear",
        layerHeights: state.layerHeights
      } satisfies DxfRackSnapshot
    ])
  );
}

function toCargoEditorAsset(asset: string): CargoEditorCargoAsset {
  if (
    asset === ModelAsset.BinCargo ||
    asset === ModelAsset.SteelCoil ||
    asset === ModelAsset.WoodenCrate
  ) {
    return asset;
  }
  throw new Error(`Unsupported persisted cargo asset: ${asset}`);
}

function disposeDxfRackCollection({ group, racks, sharedCargoTemplate }: DxfRackCollection) {
  group.removeFromParent();
  racks.forEach((rack) => {
    rack.cargoAssets.clear();
  });
  racks.clear();
  disposeRenderableObject(group);
  if (sharedCargoTemplate) {
    disposeRenderableObject(sharedCargoTemplate);
  }
}

async function createDxfDeviceCollection(
  placements: DxfDevicePlacement[],
  models: readonly DeviceModelDefinition[],
  loader: DeviceModelLoader,
  snapshots: ReadonlyMap<string, string> = new Map()
) {
  const group = new Group();
  const devices = new Map<string, DxfDeviceInstance>();
  group.name = "dxf-devices";

  try {
    for (const placement of placements) {
      const availableModels = getDeviceModelsForPrefix(models, placement.blockPrefix);
      const model =
        availableModels.find((candidate) => candidate.id === snapshots.get(placement.id)) ??
        availableModels[0];
      if (!model) {
        continue;
      }

      const device = await CadDevice.create(placement, model, loader);
      const instance = {
        blockPrefix: placement.blockPrefix,
        device,
        id: placement.id
      };
      devices.set(instance.id, instance);
      group.add(device);
    }
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { devices, group };
}

async function createDxfDoubleChainConveyorCollection(
  placements: DxfDoubleChainConveyorPlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D
) {
  const group = new Group();
  group.name = "dxf-double-chain-conveyors";

  try {
    const conveyors = await Promise.all(
      placements.map((placement) =>
        DoubleChainConveyor.create(
          {
            length: placement.length,
            position: { x: placement.positionX, y: 0, z: placement.positionZ },
            rotationY: placement.rotationY,
            width: placement.width
          },
          loader
        )
      )
    );
    conveyors.forEach((conveyor, index) => {
      const placement = placements[index];
      conveyor.name = placement?.id ?? `dxf-double-chain-conveyor-${index}`;
      if (placement) {
        applyDxfMirror(conveyor, placement);
      }
      prepareObject(conveyor);
      group.add(conveyor);
    });
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group };
}

async function createDxfRollerConveyorCollection(
  placements: DxfRollerConveyorPlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D
) {
  const group = new Group();
  group.name = "dxf-roller-conveyors";

  try {
    const conveyors = await Promise.all(
      placements.map((placement) =>
        RollerConveyor.create(
          {
            length: placement.length,
            position: { x: placement.positionX, y: 0, z: placement.positionZ },
            rotationY: placement.rotationY,
            width: placement.width
          },
          loader
        )
      )
    );
    conveyors.forEach((conveyor, index) => {
      const placement = placements[index];
      conveyor.name = placement?.id ?? `dxf-roller-conveyor-${index}`;
      if (placement) {
        applyDxfMirror(conveyor, placement);
      }
      prepareObject(conveyor);
      group.add(conveyor);
    });
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group };
}

async function createDxfRollerLiftTransferCollection(
  placements: DxfRollerLiftTransferPlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D
) {
  const group = new Group();
  group.name = "dxf-roller-lift-transfers";

  try {
    const transfers = await Promise.all(
      placements.map((placement) =>
        RollerLiftTransfer.create(
          {
            length: placement.length,
            position: { x: placement.positionX, z: placement.positionZ },
            rotationY: placement.rotationY,
            width: placement.width
          },
          loader
        )
      )
    );
    transfers.forEach((transfer, index) => {
      const placement = placements[index];
      transfer.name = placement?.id ?? `dxf-roller-lift-transfer-${index}`;
      if (placement) {
        applyDxfMirror(transfer, placement);
      }
      prepareObject(transfer);
      group.add(transfer);
    });
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group };
}

async function createDxfPalletReciprocatingLiftCollection(
  placements: DxfPalletReciprocatingLiftPlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D,
  snapshots: ReadonlyMap<string, number> = new Map()
): Promise<DxfPalletReciprocatingLiftCollection> {
  const group = new Group();
  const lifts = new Map<string, DxfPalletReciprocatingLiftInstance>();
  group.name = "dxf-pallet-reciprocating-lifts";

  try {
    for (const placement of placements) {
      const lift = await PalletReciprocatingLift.create(
        {
          layerCount: snapshots.get(placement.id) ?? 4,
          position: { x: placement.positionX, y: 0, z: placement.positionZ },
          rotationY: placement.rotationY
        },
        loader
      );
      lift.name = placement.id;
      lift.userData.dxfPalletReciprocatingLiftId = placement.id;
      applyDxfMirror(lift, placement);
      prepareObject(lift);
      const instance = { id: placement.id, lift };
      lifts.set(placement.id, instance);
      group.add(lift);
    }
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group, lifts };
}

function captureDxfPalletReciprocatingLiftSnapshots(
  collection: DxfPalletReciprocatingLiftCollection
) {
  return new Map(
    [...collection.lifts].map(([id, instance]) => [id, instance.lift.getLayerCount()])
  );
}

async function createDxfSingleStationReciprocatingShuttleCollection(
  placements: DxfSingleStationReciprocatingShuttlePlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D,
  snapshots: ReadonlyMap<string, number> = new Map()
): Promise<DxfSingleStationReciprocatingShuttleCollection> {
  const group = new Group();
  const shuttles = new Map<string, DxfSingleStationReciprocatingShuttleInstance>();
  group.name = "dxf-single-station-reciprocating-shuttles";

  try {
    for (const placement of placements) {
      const shuttle = await SingleStationReciprocatingShuttle.create(
        {
          height: snapshots.get(placement.id) ?? defaultSingleStationReciprocatingShuttleHeight,
          length: placement.length,
          position: { x: placement.positionX, y: 0, z: placement.positionZ },
          rotationY: placement.rotationY
        },
        loader
      );
      shuttle.name = placement.id;
      shuttle.userData.dxfSingleStationReciprocatingShuttleId = placement.id;
      applyDxfMirror(shuttle, placement);
      prepareObject(shuttle);
      const instance = { id: placement.id, shuttle };
      shuttles.set(placement.id, instance);
      group.add(shuttle);
    }
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group, shuttles };
}
async function createDxfPalletStackerCraneCollection(
  placements: DxfPalletStackerCranePlacement[],
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D,
  snapshots: ReadonlyMap<string, number> = new Map()
): Promise<DxfPalletStackerCraneCollection> {
  const group = new Group();
  const stackerCranes = new Map<string, DxfPalletStackerCraneInstance>();
  group.name = "dxf-pallet-stacker-cranes";

  try {
    for (const placement of placements) {
      const stackerCrane = await PalletStackerCrane.create(
        {
          height: snapshots.get(placement.id) ?? defaultPalletStackerCraneHeight,
          length: placement.length,
          position: { x: placement.positionX, y: 0, z: placement.positionZ },
          rotationY: placement.rotationY
        },
        loader
      );
      stackerCrane.name = placement.id;
      stackerCrane.userData.dxfPalletStackerCraneId = placement.id;
      applyDxfMirror(stackerCrane, placement);
      prepareObject(stackerCrane);
      const instance = { id: placement.id, stackerCrane };
      stackerCranes.set(placement.id, instance);
      group.add(stackerCrane);
    }
  } catch (error) {
    disposeRenderableObject(group);
    throw error;
  }

  return { group, stackerCranes };
}

function captureDxfSingleStationReciprocatingShuttleSnapshots(
  collection: DxfSingleStationReciprocatingShuttleCollection
) {
  return new Map(
    [...collection.shuttles].map(([id, instance]) => [id, instance.shuttle.getHeight()])
  );
}
function captureDxfPalletStackerCraneSnapshots(collection: DxfPalletStackerCraneCollection) {
  return new Map(
    [...collection.stackerCranes].map(([id, instance]) => [id, instance.stackerCrane.getHeight()])
  );
}

function disposeDxfDeviceCollection({ devices, group }: DxfDeviceCollection) {
  group.removeFromParent();
  devices.clear();
  disposeRenderableObject(group);
}

function disposeDxfDoubleChainConveyorCollection({ group }: DxfDoubleChainConveyorCollection) {
  group.removeFromParent();
  disposeRenderableObject(group);
}

function disposeDxfRollerConveyorCollection({ group }: DxfRollerConveyorCollection) {
  group.removeFromParent();
  disposeRenderableObject(group);
}

function disposeDxfRollerLiftTransferCollection({ group }: DxfRollerLiftTransferCollection) {
  group.removeFromParent();
  disposeRenderableObject(group);
}

function disposeDxfPalletReciprocatingLiftCollection({
  group,
  lifts
}: DxfPalletReciprocatingLiftCollection) {
  group.removeFromParent();
  lifts.clear();
  disposeRenderableObject(group);
}

function disposeDxfSingleStationReciprocatingShuttleCollection({
  group,
  shuttles
}: DxfSingleStationReciprocatingShuttleCollection) {
  group.removeFromParent();
  shuttles.clear();
  disposeRenderableObject(group);
}
function disposeDxfPalletStackerCraneCollection({
  group,
  stackerCranes
}: DxfPalletStackerCraneCollection) {
  group.removeFromParent();
  stackerCranes.clear();
  disposeRenderableObject(group);
}

function createSelection(
  rack: DxfRackInstance,
  cell: BinRackCargoCell | PalletRackCargoCell
): CargoEditorRackSelection {
  const cellConfiguration = rack.shelf.getCellConfiguration(cell.cellId);
  return {
    ...cell,
    cargo: Array.from({ length: cellConfiguration.cargoCapacity }, (_, slotIndex) =>
      rack.cargoAssets.get(
        createCargoAssetKey({
          cellId: cell.cellId,
          heightIndex: cell.heightIndex,
          slotIndex
        })
      )
    ),
    cargoCapacity: cellConfiguration.cargoCapacity,
    cargoCellLength: cellConfiguration.length,
    cargoCellWidth: cellConfiguration.width,
    cargoRowCount: cellConfiguration.cargoRowCount,
    layerHeights: rack.shelf.getConfiguration().layerHeights,
    lengthCellCount: rack.shelf.getCellCount(),
    kind: "rack-cell",
    rackId: rack.id,
    rackType: rack.kind
  };
}

function createDeviceSelection(
  instance: DxfDeviceInstance,
  models: readonly DeviceModelDefinition[]
): CargoEditorDeviceSelection {
  return {
    blockPrefix: instance.blockPrefix,
    deviceId: instance.id,
    kind: "device",
    modelId: instance.device.getModelId(),
    models: getDeviceModelsForPrefix(models, instance.blockPrefix)
  };
}

function createPalletReciprocatingLiftSelection(
  instance: DxfPalletReciprocatingLiftInstance
): CargoEditorPalletReciprocatingLiftSelection {
  return {
    kind: "pallet-reciprocating-lift",
    layerCount: instance.lift.getLayerCount(),
    liftId: instance.id
  };
}

function createSingleStationReciprocatingShuttleSelection(
  instance: DxfSingleStationReciprocatingShuttleInstance
): CargoEditorSingleStationReciprocatingShuttleSelection {
  return {
    height: instance.shuttle.getHeight(),
    kind: "single-station-reciprocating-shuttle",
    shuttleId: instance.id
  };
}
function createPalletStackerCraneSelection(
  instance: DxfPalletStackerCraneInstance
): CargoEditorPalletStackerCraneSelection {
  return {
    height: instance.stackerCrane.getHeight(),
    kind: "pallet-stacker-crane",
    stackerCraneId: instance.id
  };
}

function captureDxfDeviceSnapshots(collection: DxfDeviceCollection) {
  return new Map(
    [...collection.devices.values()].map((instance) => [instance.id, instance.device.getModelId()])
  );
}

function getDeviceBlockPrefixes(models: readonly DeviceModelDefinition[]) {
  return [...new Set(models.map((model) => model.blockPrefix))];
}

function areDeviceBlockPrefixesEqual(
  left: readonly DeviceModelDefinition[],
  right: readonly DeviceModelDefinition[]
) {
  const leftPrefixes = getDeviceBlockPrefixes(left).sort();
  const rightPrefixes = getDeviceBlockPrefixes(right).sort();
  return (
    leftPrefixes.length === rightPrefixes.length &&
    leftPrefixes.every((prefix, index) => prefix === rightPrefixes[index])
  );
}

function areDeviceModelsEqual(
  left: readonly DeviceModelDefinition[],
  right: readonly DeviceModelDefinition[]
) {
  return (
    left.length === right.length &&
    left.every((model, index) => {
      const candidate = right[index];
      return (
        candidate?.id === model.id &&
        candidate.blockPrefix === model.blockPrefix &&
        candidate.displayName === model.displayName &&
        candidate.url === model.url
      );
    })
  );
}

function isCargoAssetAllowed(rackType: CargoEditorRackType, asset: CargoEditorCargoAsset) {
  return rackType === "bin"
    ? asset === ModelAsset.BinCargo
    : asset === ModelAsset.WoodenCrate || asset === ModelAsset.SteelCoil;
}

function isEditableKeyboardTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName))
  );
}

function areEqualNumbers(left: readonly number[], right: readonly number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function haveSameIds(left: readonly string[], right: readonly string[]) {
  return left.length === right.length && left.every((id) => right.includes(id));
}

function yieldToMainThread() {
  return new Promise<void>((resolve) => setTimeout(resolve, 0));
}

function pruneRackCargoAssetsForLimits(
  rack: DxfRackInstance,
  cargoCapacity: number | undefined,
  layerCount: number
) {
  for (const key of rack.cargoAssets.keys()) {
    const placement = parseCargoAssetKey(key);
    const cellCapacity =
      cargoCapacity ?? rack.shelf.getCellConfiguration(placement.cellId).cargoCapacity;
    if (placement.heightIndex >= layerCount || placement.slotIndex >= cellCapacity) {
      rack.cargoAssets.delete(key);
    }
  }
}

function createRackCargoPlacements(rack: DxfRackInstance) {
  const layerCount = rack.shelf.getConfiguration().layerHeights.length;
  return rack.cells.flatMap((cell, cellIndex) => {
    const cargoCapacity = rack.shelf.getCellConfiguration(cell.id).cargoCapacity;
    return Array.from({ length: layerCount }, (_, heightIndex) =>
      Array.from({ length: cargoCapacity }, (_unused, slotIndex) => ({
        cellId: cell.id,
        cellIndex,
        heightIndex,
        slotIndex
      }))
    ).flat();
  });
}

function createCargoAssetKey({
  cellId,
  heightIndex,
  slotIndex
}: {
  cellId: string;
  heightIndex: number;
  slotIndex: number;
}) {
  return [cellId, heightIndex, slotIndex].join("\u0000");
}

function parseCargoAssetKey(key: string) {
  const [cellId, heightValue, slotValue] = key.split("\u0000");
  return {
    cellId,
    heightIndex: Number(heightValue),
    slotIndex: Number(slotValue)
  };
}

function pruneRackCargoAssets(
  rack: DxfRackInstance,
  cellId: string,
  configuration: CargoEditorRackConfiguration
) {
  rack.cargoAssets.forEach((_, key) => {
    const [cargoCellId, heightValue, slotValue] = key.split("\u0000");
    const heightIndex = Number(heightValue);
    const slotIndex = Number(slotValue);
    if (
      heightIndex >= configuration.layerHeights.length ||
      (cargoCellId === cellId && slotIndex >= configuration.cargoCapacity)
    ) {
      rack.cargoAssets.delete(key);
    }
  });
}

function resetCamera(camera: PerspectiveCamera, controls: OrbitControls) {
  camera.position.set(8, 6, 8);
  controls.target.set(0, 0, 0);
  controls.update();
}

function createAxisLabel(text: string, color: string, position: Vector3) {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "rgba(0, 0, 0, 0.62)";
    context.beginPath();
    context.roundRect(14, 14, 100, 100, 18);
    context.fill();
    context.strokeStyle = color;
    context.lineWidth = 6;
    context.stroke();
    context.fillStyle = color;
    context.font = "bold 72px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, 64, 68);
  }

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  const sprite = new Sprite(new SpriteMaterial({ map: texture, transparent: true }));
  sprite.position.copy(position);
  sprite.scale.set(0.62, 0.62, 0.62);
  return sprite;
}

function frameCameraToObject(
  camera: PerspectiveCamera,
  controls: OrbitControls,
  object: Object3D,
  width: number,
  height: number
) {
  object.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(object);
  const size = bounds.getSize(new Vector3());
  const center = bounds.getCenter(new Vector3());
  const maxSize = Math.max(size.x, size.y, size.z, 1);
  const fitHeightDistance = maxSize / (2 * Math.tan((Math.PI * camera.fov) / 360));
  const fitWidthDistance = fitHeightDistance / Math.max(width / height, 0.1);
  const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.65;

  controls.target.copy(center);
  camera.position.copy(center).add(new Vector3(distance, distance * 0.55, distance));
  camera.near = Math.max(distance / 100, 0.01);
  camera.far = distance * 100;
  camera.updateProjectionMatrix();
  controls.update();
}
